import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useSettings } from '../contexts/SettingsContext';
import { usePro } from '../contexts/ProContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePrayerSchedule } from '../contexts/PrayerScheduleContext';
import { useReadingProgress } from '../contexts/ReadingProgressContext';
import { languages } from '../i18n';
import { toast } from 'sonner';
import { VisualShareModal } from '../components/VisualShareModal';
// Tajweed Intro popup completely removed per user request.
// Previous imports: TajweedIntroModal + useTajweedIntroTrigger (deleted).

// Font Awesome for premium icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { 
  faBookQuran, 
  faHandsPraying, 
  faCompass, 
  faBookOpenReader, 
  faClock 
} from '@fortawesome/free-solid-svg-icons';

// Add icons to library
library.add(faBookQuran, faHandsPraying, faCompass, faBookOpenReader, faClock);

// Modular home components with skeleton states
import { 
  HomeHeader, HomeHeaderSkeleton,
  QuickStats, QuickStatsSkeleton,
  FeatureGrid, FeatureGridSkeleton,
  PrayerTimeCard, PrayerTimeCardSkeleton,
  DailyContent, DailyContentSkeleton,
} from '../components/home';
import MagicCoachHeroCard from '../components/home/MagicCoachHeroCard';

import {
  BookOpen,
  Compass,
  MapPin,
  Clock,
  ChevronRight,
  ChevronLeft,
  Scan,
  Flame,
  BookMarked,
  Trophy,
  Share2,
  Bookmark,
  Crown,
  Zap,
  Star,
  Eye,
  Radio,
  Lock,
  ArrowRight,
  Play,
  Sparkles,
  Moon,
  Sun,
  Palette,
  HandHeart,
  Globe,
  Mic,
  Volume2,
  Search,
  Menu,
  User,
  MoreVertical,
  Home,
  FileText,
  Bell,
  Settings,
  Download,
  Smartphone,
  Check,
  X,
  TrendingUp,
  ZoomIn,
  BookText,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMERALD_CORE_ICONS = {
  prayer: '/images/icons/emerald/prayers.png',
  hadith: '/images/icons/emerald/hadiths.png',
  duas: '/images/icons/emerald/duas.png',
  qibla: '/images/icons/emerald/qibla.png',
  quran: '/images/icons/emerald/quran.png',
};

const EMERALD_PRO_ICONS = {
  'tajweed-coach': '/images/icons/emerald/tajweed-coach.png',
  'halal-scanner': '/images/icons/emerald/halal-scanner.png',
  'masjid-guide': '/images/icons/emerald/masjid-guide.png',
  'masjid-connect': '/images/icons/emerald/masjid-connect.png',
  'dua-generator': '/images/icons/emerald/dua-generator.png',
  'smart-widget': '/images/icons/emerald/smart-widgets.png',
  'zoom-quran': '/images/icons/emerald/zoom-view.png',
  themes: '/images/icons/emerald/premium-themes.png',
};

const EMERALD_ICON_PATHS = [...Object.values(EMERALD_CORE_ICONS), ...Object.values(EMERALD_PRO_ICONS)];

// Format time for display
const formatTime12h = (time24) => {
  if (!time24) return '--:--';
  const [hours, minutes] = time24.split(':').map(Number);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${suffix}`;
};


// Famous Quran Reciters with proper images
const RECITERS = [
  { id: 'shuraim', name: 'Al-Shuraim', fullName: 'Saud Al-Shuraim', country: 'Saudi Arabia', image: '/images/reciters/shuraim.png', isFree: true },
  { id: 'sudais', name: 'Al-Sudais', fullName: 'Abdur-Rahman Al-Sudais', country: 'Saudi Arabia', image: '/images/reciters/sudais.png', isFree: false },
  { id: 'husary', name: 'Al-Husary', fullName: 'Mahmoud Khalil Al-Husary', country: 'Egypt', image: '/images/reciters/husary.png', isFree: false },
  { id: 'minshawi', name: 'Al-Minshawi', fullName: 'Mohamed Siddiq El-Minshawi', country: 'Egypt', image: '/images/reciters/minshawi.png', isFree: false },
  { id: 'alafasy', name: 'Al-Alafasy', fullName: 'Mishary Rashid Al-Alafasy', country: 'Kuwait', image: '/images/reciters/alafasy.png', isFree: false },
];

// ============================================
// AUTO-SCROLLING CAROUSEL COMPONENT
// ============================================
const AutoScrollCarousel = ({ children, speed = 30, pauseOnHover = true, direction = 'left' }) => {
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;
    
    let animationId;
    let scrollPosition = direction === 'left' ? 0 : scrollContainer.scrollWidth / 2;
    
    const animate = () => {
      if (!isPaused && scrollContainer) {
        if (direction === 'left') {
          scrollPosition += 0.5;
          if (scrollPosition >= scrollContainer.scrollWidth / 2) {
            scrollPosition = 0;
          }
        } else {
          scrollPosition -= 0.5;
          if (scrollPosition <= 0) {
            scrollPosition = scrollContainer.scrollWidth / 2;
          }
        }
        scrollContainer.scrollLeft = scrollPosition;
      }
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [isPaused, direction]);
  
  return (
    <div 
      ref={scrollRef}
      className="overflow-hidden"
      onMouseEnter={() => pauseOnHover && setIsPaused(true)}
      onMouseLeave={() => pauseOnHover && setIsPaused(false)}
      onTouchStart={() => pauseOnHover && setIsPaused(true)}
      onTouchEnd={() => pauseOnHover && setIsPaused(false)}
    >
      <div className="flex gap-4" style={{ width: 'max-content' }}>
        {children}
        {children}
      </div>
    </div>
  );
};

// Manual swipe carousel with snap + dots (Classic theme Pro Features)
const ManualSnapCarousel = ({ children }) => {
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const items = React.Children.toArray(children);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || !el.children.length) return;
    const cardWidth = el.children[0].offsetWidth + 12;
    setActiveIndex(Math.max(0, Math.min(items.length - 1, Math.round(el.scrollLeft / cardWidth))));
  };

  return (
    <div data-testid="pro-features-manual-carousel">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {items.map((child, i) => (
          <div key={i} className="snap-start flex-shrink-0">{child}</div>
        ))}
      </div>
      <div className="flex justify-center items-center gap-1.5 mt-2" data-testid="pro-features-carousel-dots">
        {items.map((_, i) => (
          <span
            key={i}
            className="rounded-full transition-all duration-300"
            style={{
              width: i === activeIndex ? '18px' : '6px',
              height: '6px',
              background: i === activeIndex
                ? 'linear-gradient(90deg, #34d399, #EAB308)'
                : 'rgba(148,163,184,0.4)',
            }}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================
// PREMIUM ROYAL THEME COMPONENTS
// ============================================

// Ornate Section Divider with stars - More Premium Look
const OrnateDivider = ({ title }) => (
  <div className="flex items-center justify-center gap-2 my-4 px-4">
    {/* Left ornament line with diamond ends - BRIGHT YELLOW, NO BLUR */}
    <div className="flex-1 flex items-center gap-1">
      <div className="w-1.5 h-1.5 rotate-45 bg-[#facc15]" />
      <div className="flex-1 h-[1px] bg-[#facc15]/70" />
    </div>
    
    {/* Center title with decorative frame */}
    <div className="relative px-4 py-0.5">
      {/* Corner accents - bright yellow */}
      <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-[#facc15]" />
      <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-[#facc15]" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-[#facc15]" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-[#facc15]" />
      
      {/* CRISP TEXT - no blur, bold, bright yellow */}
      <span 
        className="text-xs font-bold tracking-[0.12em] uppercase"
        style={{ 
          color: '#facc15',
          fontFamily: "'Playfair Display', serif",
        }}
      >
        {title}
      </span>
    </div>
    
    {/* Right ornament line with diamond ends - BRIGHT YELLOW, NO BLUR */}
    <div className="flex-1 flex items-center gap-1">
      <div className="flex-1 h-[1px] bg-[#facc15]/70" />
      <div className="w-1.5 h-1.5 rotate-45 bg-[#facc15]" />
    </div>
  </div>
);

// Premium Core Feature Icon Button - Elegant circular design with glow
const PremiumCoreFeatureIcon = ({ icon: Icon, label, path, index }) => {
  const iconColors = [
    { bg: 'linear-gradient(180deg, #c9a227 0%, #a88b1e 100%)', glow: 'rgba(201, 162, 39, 0.5)' }, // Gold - Quran
    { bg: 'linear-gradient(180deg, #7c3aed 0%, #5b21b6 100%)', glow: 'rgba(124, 58, 237, 0.4)' }, // Purple - Duas
    { bg: 'linear-gradient(180deg, #10b981 0%, #059669 100%)', glow: 'rgba(16, 185, 129, 0.4)' }, // Emerald - Qibla
    { bg: 'linear-gradient(180deg, #c9a227 0%, #a88b1e 100%)', glow: 'rgba(201, 162, 39, 0.5)' }, // Gold - Hadith
    { bg: 'linear-gradient(180deg, #7c3aed 0%, #5b21b6 100%)', glow: 'rgba(124, 58, 237, 0.4)' }, // Purple - Prayer
  ];
  const colorSet = iconColors[index % iconColors.length];
  
  return (
    <Link 
      to={path}
      className="flex-shrink-0 flex flex-col items-center gap-2 group"
    >
      {/* Outer glow ring */}
      <div 
        className="relative p-[2px] rounded-full transition-all duration-300 group-hover:scale-110"
        style={{
          background: `linear-gradient(180deg, rgba(201, 162, 39, 0.6) 0%, rgba(201, 162, 39, 0.2) 100%)`,
          boxShadow: `0 0 25px ${colorSet.glow}, inset 0 0 15px rgba(201, 162, 39, 0.1)`,
        }}
      >
        {/* Inner dark circle with icon */}
        <div 
          className="w-14 h-14 rounded-full flex items-center justify-center relative overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #2a2a4a 0%, #1a1a2e 100%)',
          }}
        >
          {/* Subtle inner glow */}
          <div 
            className="absolute inset-0 rounded-full"
            style={{
              background: `radial-gradient(circle at 30% 30%, rgba(201, 162, 39, 0.15) 0%, transparent 60%)`,
            }}
          />
          <Icon 
            className="w-6 h-6 relative z-10 transition-all duration-300 group-hover:scale-110" 
            style={{ color: '#c9a227', filter: 'drop-shadow(0 0 8px rgba(201, 162, 39, 0.6))' }}
            strokeWidth={1.5} 
          />
        </div>
      </div>
      {/* Label */}
      <span 
        className="text-[11px] font-medium tracking-wide transition-colors duration-300"
        style={{ 
          color: 'rgba(201, 162, 39, 0.9)',
          textShadow: '0 0 10px rgba(201, 162, 39, 0.3)',
        }}
      >
        {label}
      </span>
    </Link>
  );
};

// Premium Feature Card - Dark glass-morphism style for Royal Night
const PremiumFeatureCard = ({ icon: Icon, title, description, onClick, isActive }) => {
  return (
    <div 
      className="relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:scale-[1.03] group"
      onClick={onClick}
      style={{
        background: 'linear-gradient(180deg, rgba(42, 42, 74, 0.9) 0%, rgba(26, 26, 46, 0.95) 100%)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(201, 162, 39, 0.4)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(201, 162, 39, 0.2)',
      }}
    >
      {/* Premium corner accents */}
      <div className="absolute top-0 left-0 w-6 h-6">
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#c9a227] to-transparent" />
        <div className="absolute top-0 left-0 h-full w-[1px] bg-gradient-to-b from-[#c9a227] to-transparent" />
      </div>
      <div className="absolute top-0 right-0 w-6 h-6">
        <div className="absolute top-0 right-0 w-full h-[1px] bg-gradient-to-l from-[#c9a227] to-transparent" />
        <div className="absolute top-0 right-0 h-full w-[1px] bg-gradient-to-b from-[#c9a227] to-transparent" />
      </div>
      <div className="absolute bottom-0 left-0 w-6 h-6">
        <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#c9a227] to-transparent" />
        <div className="absolute bottom-0 left-0 h-full w-[1px] bg-gradient-to-t from-[#c9a227] to-transparent" />
      </div>
      <div className="absolute bottom-0 right-0 w-6 h-6">
        <div className="absolute bottom-0 right-0 w-full h-[1px] bg-gradient-to-l from-[#c9a227] to-transparent" />
        <div className="absolute bottom-0 right-0 h-full w-[1px] bg-gradient-to-t from-[#c9a227] to-transparent" />
      </div>
      
      {/* Hover glow effect */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(201, 162, 39, 0.1) 0%, transparent 70%)',
        }}
      />
      
      {/* Pro badge */}
      <div 
        className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider"
        style={{
          background: 'linear-gradient(135deg, #c9a227 0%, #a88b1e 100%)',
          color: '#1a1a2e',
          boxShadow: '0 2px 10px rgba(201, 162, 39, 0.4)',
        }}
      >
        PRO
      </div>
      
      <div className="p-4 relative">
        <div className="flex items-center gap-4">
          {/* Icon with golden glow */}
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 relative"
            style={{
              background: 'linear-gradient(180deg, rgba(201, 162, 39, 0.15) 0%, rgba(201, 162, 39, 0.05) 100%)',
              border: '1px solid rgba(201, 162, 39, 0.4)',
              boxShadow: '0 0 20px rgba(201, 162, 39, 0.2)',
            }}
          >
            <Icon className="w-6 h-6 text-[#c9a227]" strokeWidth={1.5} style={{ filter: 'drop-shadow(0 0 6px rgba(201, 162, 39, 0.5))' }} />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold mb-0.5 text-[#c9a227]">{title}</h3>
            <p className="text-xs leading-snug text-white/60">{description}</p>
          </div>
          
          <ChevronRight className="w-5 h-5 text-[#c9a227]/60 flex-shrink-0 group-hover:text-[#c9a227] group-hover:translate-x-1 transition-all duration-300" />
        </div>
      </div>
    </div>
  );
};

// Reciter Card with circular image and green outline
const ReciterCard = ({ reciter, onClick }) => {
  return (
    <div 
      className="flex-shrink-0 w-24 cursor-pointer transition-all duration-300 hover:scale-105 text-center"
      onClick={onClick}
    >
      {/* Circular reciter photo with green outline */}
      <div 
        className="w-16 h-16 mx-auto rounded-full overflow-hidden"
        style={{
          border: '2px solid #10b981',
          boxShadow: '0 2px 10px rgba(16, 185, 129, 0.2)',
        }}
      >
        <img 
          src={reciter.image} 
          alt={reciter.fullName}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="mt-2">
        <p className="font-bold text-xs truncate text-[#1a1a2e]">{reciter.name}</p>
        <p className="text-[10px] truncate text-[#666]">{reciter.country}</p>
      </div>
    </div>
  );
};

// Premium Verse of the Day Card
const VerseOfDayCard = ({ verse, onSave, onShare }) => (
  <div 
    className="relative rounded-2xl overflow-hidden"
    style={{
      background: 'linear-gradient(180deg, #fef6e3 0%, #fff8ed 100%)',
      border: '2px solid #c9a227',
      boxShadow: '0 4px 20px rgba(201, 162, 39, 0.15)',
    }}
  >
    {/* Decorative corners */}
    <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-[#c9a227]" />
    <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-[#c9a227]" />
    <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-[#c9a227]" />
    <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-[#c9a227]" />
    
    <div className="p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#c9a227]/20 flex items-center justify-center">
            <Star className="w-4 h-4 text-[#c9a227] fill-[#c9a227]" />
          </div>
          <span className="font-bold text-[#1a1a2e]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Verse of the Day
          </span>
        </div>
        <span className="text-xs text-[#888]">{verse?.surahName} · {verse?.ayah}</span>
      </div>
      
      {/* Arabic Text */}
      {verse?.text && (
        <p 
          className="text-center text-xl leading-[2.2] mb-4 text-[#1a1a2e]" 
          style={{ fontFamily: "'Amiri', 'Noto Naskh Arabic', serif", direction: 'rtl' }}
        >
          {verse.text}
        </p>
      )}
      
      {/* Translation */}
      {verse?.translation && (
        <p className="text-sm italic text-center text-[#555] leading-relaxed mb-4">
          "{verse.translation}"
        </p>
      )}
      
      {/* Gold divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#c9a227] to-transparent my-4" />
      
      {/* Actions */}
      <div className="flex items-center justify-between">
        <Link to={`/quran/${verse?.surah || 1}`} className="text-sm font-medium text-purple-600 flex items-center gap-1">
          Read Full Surah <ChevronRight className="w-4 h-4" />
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={onSave} className="p-2 rounded-full hover:bg-[#c9a227]/10 transition-colors">
            <Bookmark className="w-5 h-5 text-[#c9a227]" />
          </button>
          <button onClick={onShare} className="p-2 rounded-full hover:bg-[#c9a227]/10 transition-colors">
            <Share2 className="w-5 h-5 text-[#c9a227]" />
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Premium Quick Stats Card - Links to Progress Page
const QuickStatsCard = ({ stats, onClick }) => {
  const { t } = useTranslation();
  return (
  <div 
    className="relative rounded-xl overflow-hidden cursor-pointer transition-transform hover:scale-[1.02]"
    onClick={onClick}
    style={{
      background: 'linear-gradient(180deg, #fef6e3 0%, #fff8ed 100%)',
      border: '2px solid #c9a227',
      boxShadow: '0 4px 20px rgba(201, 162, 39, 0.15)',
    }}
    data-testid="quick-stats-card"
  >
    {/* Inner corner flourishes */}
    <div className="absolute top-0 left-0 w-5 h-5 border-b border-r border-[#c9a227]/40 rounded-br-xl z-10" />
    <div className="absolute top-0 right-0 w-5 h-5 border-b border-l border-[#c9a227]/40 rounded-bl-xl z-10" />
    <div className="absolute bottom-0 left-0 w-5 h-5 border-t border-r border-[#c9a227]/40 rounded-tr-xl z-10" />
    <div className="absolute bottom-0 right-0 w-5 h-5 border-t border-l border-[#c9a227]/40 rounded-tl-xl z-10" />
    
    <div className="p-5">
      <div className="grid grid-cols-3 gap-4">
        {/* Day Streak */}
        <div className="text-center">
          <div 
            className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-2"
            style={{
              border: '2px solid #7c3aed',
              boxShadow: '0 0 15px rgba(124, 58, 237, 0.2)',
              background: 'rgba(124, 58, 237, 0.05)',
            }}
          >
            <Flame className="w-7 h-7 text-purple-600" strokeWidth={1.5} />
          </div>
          <p className="text-2xl font-bold text-[#1a1a2e]">{stats.streak}</p>
          <p className="text-xs text-[#888]">{t('home.dayStreak', "Day Streak")}</p>
        </div>
        
        {/* Total Time */}
        <div className="text-center">
          <div 
            className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-2"
            style={{
              border: '2px solid #7c3aed',
              boxShadow: '0 0 15px rgba(124, 58, 237, 0.2)',
              background: 'rgba(124, 58, 237, 0.05)',
            }}
          >
            <Clock className="w-7 h-7 text-purple-600" strokeWidth={1.5} />
          </div>
          <p className="text-2xl font-bold text-[#1a1a2e]">{stats.totalTime}</p>
          <p className="text-xs text-[#888]">{t('home.totalTime', "Total Time")}</p>
        </div>
        
        {/* Surahs Completed */}
        <div className="text-center">
          <div 
            className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-2"
            style={{
              border: '2px solid #7c3aed',
              boxShadow: '0 0 15px rgba(124, 58, 237, 0.2)',
              background: 'rgba(124, 58, 237, 0.05)',
            }}
          >
            <BookOpen className="w-7 h-7 text-purple-600" strokeWidth={1.5} />
          </div>
          <p className="text-2xl font-bold text-[#1a1a2e]">{stats.surahs}/114</p>
          <p className="text-xs text-[#888]">Surahs</p>
        </div>
      </div>
      
      {/* Tap to view more */}
      <div className="flex items-center justify-center gap-1 mt-3 text-xs text-[#7c3aed]">
        <span>{t('home.tapToView', "Tap to view progress")}</span>
        <ChevronRight className="w-3 h-3" />
      </div>
    </div>
  </div>
  );
};

// Today's Story Card - Minimal Netflix-style card that links to Daily Stories page
// Theme-aware: accepts colors to match homepage theme
const TodaysStoryCard = ({ navigate, isKids = false, themeColors = null, t, currentLang = 'en', onShare }) => {
  const [todayStory, setTodayStory] = useState({ title: '...', preview: '...' });
  
  // Default colors (Emerald/gold style)
  const colors = themeColors || {
    border: '#c9a227',
    headerBg: 'linear-gradient(135deg, #0a3d2c 0%, #115e42 100%)',
    headerAccent: '#c9a227',
    cardBg: '#fef6e3',
    titleColor: '#1a1a2e',
    previewColor: '#555',
    linkColor: '#c9a227',
  };
  
  // Import stories data for fallback
  const storiesData = require('../data/dailyStories.json');
  
  // Get local fallback story
  const getLocalStory = () => {
    const today = new Date();
    const dateString = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
    let hash = 0;
    for (let i = 0; i < dateString.length; i++) {
      hash = ((hash << 5) - hash) + dateString.charCodeAt(i);
      hash = hash & hash;
    }
    const storyIndex = Math.abs(hash) % storiesData.length;
    const story = storiesData[storyIndex];
    const lang = currentLang || 'en';
    return {
      id: story.id,
      title: story.title[lang] || story.title.en,
      preview: story.preview[lang] || story.preview.en,
    };
  };
  
  // Fetch story from API on mount
  useEffect(() => {
    const fetchStory = async () => {
      try {
        // Pass current language to API for translated content
        const langCode = currentLang?.split('-')[0] || 'en';
        const response = await axios.get(`${API}/daily-stories/today?lang=${langCode}`);
        if (response.data?.story) {
          const s = response.data.story;
          // Handle both old format {en: "...", ar: "..."} and new format "..."
          const getTextValue = (val) => {
            if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
              return val[langCode] || val.en || Object.values(val)[0] || '';
            }
            return val || '';
          };
          setTodayStory({
            id: response.data.index,
            title: getTextValue(s.title) || 'Daily Story',
            preview: getTextValue(s.subtitle) || '',
            content: getTextValue(s.content) || '',
          });
          return;
        }
      } catch (e) {}
      // Fallback
      setTodayStory(getLocalStory());
    };
    fetchStory();
  }, [currentLang]);
  
  // Kids theme shows "Coming Soon"
  if (isKids) {
    return (
      <div 
        className="relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02]"
        onClick={() => navigate('/daily-stories')}
        style={{
          border: '3px solid #ec4899',
          boxShadow: '0 4px 20px rgba(236, 72, 153, 0.2)',
        }}
      >
        <div className="bg-[#fdf2f8] p-5 text-center">
          <span className="text-4xl mb-2 block">🌟</span>
          <h3 className="font-bold text-lg text-[#831843]">{t('home.kidsStories', 'Kids Stories')}</h3>
          <p className="text-sm text-[#9d174d] mt-1">{t('home.kidsStoriesDesc', 'Magical bedtime stories!')}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02]"
      onClick={() => navigate('/daily-stories')}
      style={{
        border: `2px solid ${colors.border}`,
        boxShadow: `0 4px 20px ${colors.border}30`,
      }}
      data-testid="todays-story-card"
    >
      {/* Inner corner flourishes */}
      <div className="absolute top-0 left-0 w-5 h-5 border-b border-r rounded-br-xl z-10" style={{ borderColor: `${colors.border}60` }} />
      <div className="absolute top-0 right-0 w-5 h-5 border-b border-l rounded-bl-xl z-10" style={{ borderColor: `${colors.border}60` }} />
      <div className="absolute bottom-0 left-0 w-5 h-5 border-t border-r rounded-tr-xl z-10" style={{ borderColor: `${colors.border}60` }} />
      <div className="absolute bottom-0 right-0 w-5 h-5 border-t border-l rounded-tl-xl z-10" style={{ borderColor: `${colors.border}60` }} />
      
      {/* Gradient Header */}
      <div 
        className="h-24 relative overflow-hidden"
        style={{ background: colors.headerBg }}
      >
        <div className="absolute inset-0 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" style={{ color: colors.headerAccent }} />
            <span className="text-sm font-semibold uppercase tracking-wide" style={{ color: colors.headerAccent }}>{t('home.todaysStoryHeader', "TODAY'S STORY")}</span>
          </div>
          <ChevronRight className="w-5 h-5" style={{ color: colors.headerAccent }} />
        </div>
      </div>
      
      {/* Story Preview */}
      <div className="p-4" style={{ background: colors.cardBg }} data-testid="story-card-body">
        <h3 
          className="font-bold text-base mb-2 line-clamp-1 royal-story-title" 
          style={{ color: colors.titleColor }}
          data-testid="story-title"
        >
          {todayStory.title}
        </h3>
        <p className="text-sm italic line-clamp-2" style={{ color: colors.previewColor }}>
          "{todayStory.preview}"
        </p>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center text-xs font-medium" style={{ color: colors.linkColor }}>
            <span>{t('home.readFullStory', 'Read full story')}</span>
            <ChevronRight className="w-4 h-4 ml-1" />
          </div>
          {onShare && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onShare(todayStory);
              }}
              className="p-1.5 rounded-full hover:bg-black/5 transition-colors"
              data-testid="share-story-home-btn"
              title={t('common.share', 'Share')}
            >
              <Share2 className="w-4 h-4" style={{ color: colors.linkColor }} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Legacy Today's Inspiration Card (keeping for reference)
const TodaysInspirationCard = () => (
  <div 
    className="relative rounded-xl overflow-hidden"
    style={{
      border: '2px solid #c9a227',
      boxShadow: '0 4px 20px rgba(201, 162, 39, 0.2)',
    }}
  >
    {/* Inner corner flourishes */}
    <div className="absolute top-0 left-0 w-5 h-5 border-b border-r border-[#c9a227]/40 rounded-br-xl z-10" />
    <div className="absolute top-0 right-0 w-5 h-5 border-b border-l border-[#c9a227]/40 rounded-bl-xl z-10" />
    <div className="absolute bottom-0 left-0 w-5 h-5 border-t border-r border-[#c9a227]/40 rounded-tr-xl z-10" />
    <div className="absolute bottom-0 right-0 w-5 h-5 border-t border-l border-[#c9a227]/40 rounded-tl-xl z-10" />
    
    {/* Mosque Image Header */}
    <div className="h-40 relative overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1564769625905-50e93615e769?w=800')`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a2e]/80 to-transparent" />
      
      {/* Title overlay */}
      <div className="absolute bottom-4 left-4 right-4">
        <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
          Today's Inspiration
        </h3>
      </div>
    </div>
    
    {/* Action buttons */}
    <div className="bg-[#fef6e3] px-4 py-3 flex items-center justify-around border-t border-[#c9a227]/30">
      <button className="flex items-center gap-2 text-[#555] text-sm">
        <FileText className="w-4 h-4 text-purple-600" />
        Tagum
      </button>
      <div className="w-px h-4 bg-[#c9a227]/30" />
      <button className="flex items-center gap-2 text-[#555] text-sm">
        <BookOpen className="w-4 h-4 text-purple-600" />
        Learn
      </button>
      <div className="w-px h-4 bg-[#c9a227]/30" />
      <button className="flex items-center gap-2 text-[#555] text-sm">
        <ChevronRight className="w-4 h-4 text-purple-600" />
        Swipe
      </button>
    </div>
    
    {/* Carousel dots */}
    <div className="bg-[#fef6e3] pb-3 flex justify-center gap-1.5">
      <div className="w-6 h-1.5 rounded-full bg-[#c9a227]" />
      <div className="w-1.5 h-1.5 rounded-full bg-[#c9a227]/30" />
      <div className="w-1.5 h-1.5 rounded-full bg-[#c9a227]/30" />
    </div>
  </div>
);

// Daily Inspiration Expandable Section
const DailyInspirationSection = ({ hadith, onShare, t }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div 
      className="rounded-2xl overflow-hidden cursor-pointer"
      style={{
        background: 'linear-gradient(135deg, #2d1f4e 0%, #1a1a2e 100%)',
        border: '1px solid rgba(201, 162, 39, 0.3)',
      }}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Moon className="w-5 h-5 text-[#c9a227]" />
          <span className="text-white font-medium">{t('home.dailyInspiration', 'Daily Inspiration')}</span>
        </div>
        <ChevronRight className={`w-5 h-5 text-[#c9a227] transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </div>
      
      {expanded && (
        <div className="px-4 pb-4 border-t border-[#c9a227]/20">
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center">
                <Bell className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-white font-medium">{t('home.hadithOfDay', 'Hadith of the Day')}</p>
                <p className="text-purple-300/70 text-xs">{hadith?.book || 'Sahih al-Bukhari'}</p>
              </div>
            </div>
            {onShare && hadith && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onShare(hadith);
                }}
                className="p-2 rounded-full bg-[#c9a227]/20 hover:bg-[#c9a227]/30 transition-colors"
                data-testid="share-hadith-home-btn"
                title={t ? t('common.share', 'Share') : 'Share'}
              >
                <Share2 className="w-4 h-4 text-[#c9a227]" />
              </button>
            )}
          </div>
          {hadith?.text && (
            <p className="text-purple-200/80 text-sm mt-3 leading-relaxed">{hadith.text}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// KIDS PRO MODAL COMPONENT
// ============================================
const KidsProModal = ({ isOpen, onClose, isDark, t }) => {
  if (!isOpen) return null;
  
  const kidsAccent = isDark ? '#fbbf24' : '#DB2777';
  const kidsTextPrimary = isDark ? '#ffffff' : '#4C1D95';
  
  const features = [
    { emoji: '📖', label: t('kids.talkingStories', 'Talking Story Books'), comingSoon: false },
    { emoji: '📖', label: t('kids.quranCoach', 'Quran Coach'), comingSoon: false },
    { emoji: '🔍', label: t('kids.halalScanner', 'Halal Scanner'), comingSoon: false },
    { emoji: '🏆', label: t('kids.rewardsBadges', 'Rewards & Badges'), comingSoon: false },
  ];
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ 
          background: isDark ? '#3730a3' : '#FCE7F3',
          border: `4px solid ${kidsAccent}`,
        }}
      >
        {/* Header */}
        <div 
          className="p-5 flex items-center gap-4"
          style={{ 
            background: isDark 
              ? 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' 
              : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' 
          }}
        >
          <span className="text-4xl">👑</span>
          <div>
            <h3 className="text-white font-bold text-xl">{t('kids.askParents', 'Ask Your Parents!')}</h3>
            <p className="text-white/80 text-sm">{t('kids.unlockMore', 'Unlock more fun features')}</p>
          </div>
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 text-white/70 hover:text-white text-2xl font-bold"
          >
            ×
          </button>
        </div>
        
        {/* Features Grid */}
        <div className="p-5">
          <div className="grid grid-cols-2 gap-3 mb-5">
            {features.map((feature, idx) => (
              <div 
                key={idx} 
                className="relative flex items-center gap-2 p-3 rounded-xl"
                style={{ background: isDark ? 'rgba(251,191,36,0.15)' : 'rgba(251,191,36,0.25)' }}
              >
                <span className="text-2xl">{feature.emoji}</span>
                <div className="flex-1">
                  <span className="text-sm font-bold block" style={{ color: kidsTextPrimary }}>{feature.label}</span>
                  {feature.comingSoon && (
                    <span className="text-[10px] font-medium" style={{ color: kidsAccent }}>{t('common.comingSoon', 'Coming Soon!')}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Get Premium Button */}
          <button 
            onClick={onClose}
            className="w-full py-4 rounded-2xl flex items-center justify-center gap-2 text-white font-bold text-lg transition-transform hover:scale-[1.02]"
            style={{ 
              background: isDark 
                ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' 
                : 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' 
            }}
          >
            <span className="text-xl">✨</span>
            {t('kids.getPremium', 'Get Premium!')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN HOMEPAGE COMPONENT
// ============================================
const HomePage = () => {
  const { t, i18n } = useTranslation();
  const { settings, updateSettings } = useSettings();
  const { isPro, promptUpgrade, requestFeatureAccess, isFeatureUnlockedViaAd, PRO_FEATURES } = usePro();
  const { mode, appTheme, setAppTheme, themes: APP_THEMES } = useTheme();
  const { progress } = useReadingProgress();
  const navigate = useNavigate();
  const routerLocation = useLocation();

  // ============================================
  // TAJWEED AI COACH ONBOARDING MODAL — REMOVED
  // Popup completely removed per user request. Hook call and all
  // {tajweedIntro.modal} renders below have been deleted.
  // ============================================
  
  // Kids Pro Modal state
  const [showKidsProModal, setShowKidsProModal] = useState(false);
  
  // Visual Share Modal state
  const [showVisualShare, setShowVisualShare] = useState(false);
  const [visualShareData, setVisualShareData] = useState(null);
  
  // Open Visual Share for Story - WITH NULL GUARD
  const openStoryShare = (story) => {
    // Guard clause - prevent execution if story is null/undefined
    if (!story) {
      console.warn('openStoryShare called with null/undefined story');
      return;
    }

    // Some daily-story records ship with an empty `preview` (subtitle)
    // field. Fall back to the first sentence of `content` so the card
    // always has body text — otherwise VisualShareModal aborts with
    // "No content to share" and the user only sees the app name in
    // the Android share sheet (root cause of bug #6B).
    let previewText = (story.preview || '').trim();
    if (!previewText && story.content) {
      const firstSentence = String(story.content).split(/(?<=[.!?])\s+/)[0] || '';
      previewText = firstSentence.slice(0, 240).trim();
    }
    if (!previewText) previewText = story.title || 'Daily Story';

    setVisualShareData({
      contentType: 'story',
      title: story.title || 'Daily Story',
      arabicText: '',
      translationText: previewText,
      transliterationText: '',
      reference: '',
    });
    setShowVisualShare(true);
  };
  
  // Open Visual Share for Hadith - WITH NULL GUARD
  const openHadithShare = (hadith) => {
    // Guard clause - prevent execution if hadith is null/undefined
    if (!hadith) {
      console.warn('openHadithShare called with null/undefined hadith');
      return;
    }
    
    setVisualShareData({
      contentType: 'hadith',
      title: `Hadith of the Day`,
      arabicText: hadith.arabic || '',
      translationText: hadith.text || '',
      transliterationText: '',
      reference: hadith.book || 'Sahih al-Bukhari',
    });
    setShowVisualShare(true);
  };
  
  const isDark = mode === 'dark';
  const isRoyal = appTheme === 'royal';
  const isEmerald = appTheme === 'emerald';

  // The Emerald feature medallions are page-critical imagery. Start their requests
  // when the Emerald homepage mounts, rather than waiting for lazy image discovery.
  useEffect(() => {
    if (!isEmerald) return;

    EMERALD_ICON_PATHS.forEach((href, index) => {
      if (document.head.querySelector(`link[data-emerald-icon-preload="${href}"]`)) return;

      const preload = document.createElement('link');
      preload.rel = 'preload';
      preload.as = 'image';
      preload.href = href;
      preload.setAttribute('fetchpriority', index < Object.keys(EMERALD_CORE_ICONS).length ? 'high' : 'low');
      preload.dataset.emeraldIconPreload = href;
      document.head.appendChild(preload);
    });
  }, [isEmerald]);
  
  // Helper function to format time from seconds
  const formatTotalTime = (seconds) => {
    if (!seconds || seconds === 0) return '0m';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };
  
  // Get real stats from Reading Progress Context
  const stats = {
    streak: progress?.currentStreak || 0,
    totalTime: formatTotalTime(progress?.totalTimeSpent || 0),
    surahs: progress?.surahsCompleted?.length || 0
  };
  
  // State
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [nextPrayer, setNextPrayer] = useState(null);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [dailyVerse, setDailyVerse] = useState(null);
  const [dailyHadith, setDailyHadith] = useState(null);
  const [location, setLocation] = useState(null);
  const [mosqueConnected, setMosqueConnected] = useState(null);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);

  // Load mosque data from localStorage (stats come from ReadingProgressContext)
  useEffect(() => {
    const savedMosque = localStorage.getItem('quranplus-connected-mosque');
    if (savedMosque) {
      try { setMosqueConnected(JSON.parse(savedMosque)); } catch (e) {}
    }
  }, []);

  // Get user location
  useEffect(() => {
    const savedLocation = localStorage.getItem('quranplus-user-location');
    if (savedLocation) {
      try { setLocation(JSON.parse(savedLocation)); return; } catch (e) {}
    }
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
          setLocation(loc);
          localStorage.setItem('quranplus-user-location', JSON.stringify(loc));
        },
        () => setLocation({ lat: 51.5074, lng: -0.1278 })
      );
    }
  }, []);

  // Fetch prayer times from the SHARED PrayerScheduleContext (source of truth
  // for the whole app - stays in sync with the /prayer-times timetable,
  // whether the user is in calculation OR live-sync mosque mode).
  const { activeTimetable } = usePrayerSchedule();

  useEffect(() => {
    if (!activeTimetable) {
      setPrayerTimes(null);
      return;
    }
    const pick = (entry) => {
      if (!entry) return null;
      const raw = entry.time || entry.jamaah || entry.start;
      return raw ? String(raw).split(' ')[0] : null;
    };
    const times = {
      Fajr:    pick(activeTimetable.fajr),
      Sunrise: pick(activeTimetable.sunrise),
      Dhuhr:   pick(activeTimetable.dhuhr),
      Asr:     pick(activeTimetable.asr),
      Maghrib: pick(activeTimetable.maghrib),
      Isha:    pick(activeTimetable.isha),
    };
    setPrayerTimes(times);
  }, [activeTimetable]);

  // Calculate next prayer
  useEffect(() => {
    if (!prayerTimes) return;
    const calculateNextPrayer = () => {
      const now = new Date();
      const prayers = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
      for (const prayer of prayers) {
        const time = prayerTimes[prayer];
        if (!time) continue;
        const [hours, minutes] = time.split(':').map(Number);
        const prayerDate = new Date();
        prayerDate.setHours(hours, minutes, 0, 0);
        if (now < prayerDate) {
          setNextPrayer({ name: prayer, time });
          return;
        }
      }
      setNextPrayer({ name: 'Fajr', time: prayerTimes.Fajr });
    };
    calculateNextPrayer();
    const interval = setInterval(calculateNextPrayer, 60000);
    return () => clearInterval(interval);
  }, [prayerTimes]);

  // Countdown timer
  useEffect(() => {
    if (!nextPrayer?.time) return;
    const updateCountdown = () => {
      const now = new Date();
      const [hours, minutes] = nextPrayer.time.split(':').map(Number);
      const prayerDate = new Date();
      prayerDate.setHours(hours, minutes, 0, 0);
      if (now > prayerDate) prayerDate.setDate(prayerDate.getDate() + 1);
      const diff = prayerDate - now;
      setCountdown({
        hours: Math.floor(diff / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      });
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [nextPrayer]);

  // Fetch daily verse - use the current app language for translation
  useEffect(() => {
    const fetchDailyVerse = async () => {
      const today = new Date().toDateString();
      const currentLang = i18n.language || 'en';
      const cacheKey = `quranplus-daily-verse-${currentLang}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const { date, verse } = JSON.parse(cached);
          if (date === today) { setDailyVerse(verse); return; }
        } catch (e) {}
      }
      try {
        const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
        const surah = (dayOfYear % 114) + 1;
        const ayah = (dayOfYear % 7) + 1;
        // Use the current app language for translation
        const translationId = currentLang === 'ar' ? 'ar.muyassar' : currentLang === 'fr' ? 'fr.hamidullah' : 'en.sahih';
        const response = await axios.get(`${API}/quran/verse/${surah}/${ayah}`, { params: { translation: translationId } });
        if (response.data?.verse) {
          const verse = {
            surah, ayah,
            surahName: response.data.surah?.name_simple || `Surah ${surah}`,
            text: response.data.verse.text_uthmani || '',
            translation: response.data.verse.translations?.[0]?.text || '',
          };
          setDailyVerse(verse);
          localStorage.setItem(cacheKey, JSON.stringify({ date: today, verse }));
        }
      } catch (error) {
        setDailyVerse({
          surah: 98, ayah: 7, surahName: 'Al-Bayyinah',
          text: 'إِنَّ الَّذِينَ آمَنُوا وَعَمِلُوا الصَّالِحَاتِ أُولَٰئِكَ هُمْ خَيْرُ الْبَرِيَّةِ',
          translation: 'Indeed, they who have believed and done righteous deeds - those are the best of creatures.',
        });
      }
    };
    fetchDailyVerse();
  }, [i18n.language]);

  // Fetch daily hadith from API - updates at midnight based on day of year
  // NOTE: cache key is versioned (`v2`) to invalidate legacy caches that may
  // have been polluted by the old 5-hadith fallback path.
  useEffect(() => {
    const currentLang = i18n.language || 'en';

    const fetchDailyHadith = async () => {
      const today = new Date().toDateString();
      const cacheKey = `quranplus-daily-hadith-v2-${currentLang}`;

      // Self-heal: purge legacy v1 cache once
      try { localStorage.removeItem(`quranplus-daily-hadith-${currentLang}`); } catch {}

      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const { date, hadith } = JSON.parse(cached);
          if (date === today && hadith) {
            let hadithText = hadith.text;
            if (typeof hadithText === 'object' && hadithText !== null) {
              const langCode = currentLang?.split('-')[0] || 'en';
              hadithText =
                hadithText[langCode] || hadithText.en || Object.values(hadithText)[0] || '';
              hadith.text = hadithText;
            }
            setDailyHadith(hadith);
            return;
          }
        } catch (e) {
          localStorage.removeItem(cacheKey);
        }
      }

      // ONLY the API drives what the user sees. No hardcoded fallback list.
      // If the API is unavailable OR returns null (e.g., DB not seeded),
      // fall back to the bundled 50-hadith JSON so the card is never empty.
      try {
        const langCode = currentLang?.split('-')[0] || 'en';
        const response = await axios.get(`${API}/daily-hadiths/today?lang=${langCode}`);
        if (response.data?.hadith) {
          const apiHadith = response.data.hadith;
          let hadithText = apiHadith.text;
          if (typeof hadithText === 'object' && hadithText !== null) {
            hadithText =
              hadithText[langCode] || hadithText.en || Object.values(hadithText)[0] || '';
          }
          const hadith = {
            text: hadithText,
            book: apiHadith.book,
            number: apiHadith.number,
          };
          setDailyHadith(hadith);
          localStorage.setItem(cacheKey, JSON.stringify({ date: today, hadith }));
          return;
        }
      } catch (e) {
        console.warn('[daily-hadith] API failed, using offline JSON fallback', e);
      }

      // API returned null OR threw. Use the bundled 50-hadith JSON so the
      // card is never blank (identical strategy to daily stories).
      try {
        const hadithsData = require('../data/dailyHadiths.json');
        if (hadithsData?.length) {
          const dayOfYear = Math.floor(
            (new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24)
          );
          const idx = dayOfYear % hadithsData.length;
          const fh = hadithsData[idx];
          const langCode = currentLang?.split('-')[0] || 'en';
          const hadithText =
            (typeof fh.text === 'object' ? (fh.text[langCode] || fh.text.en) : fh.text) || '';
          const hadith = { text: hadithText, book: fh.book, number: fh.number };
          setDailyHadith(hadith);
          // Do NOT cache the offline fallback - so the next open retries the API.
        }
      } catch (fallbackErr) {
        console.error('[daily-hadith] offline fallback also failed', fallbackErr);
      }
    };
    fetchDailyHadith();

    // Midnight refresh — clear cache and refetch so the day boundary works
    // even for long-lived sessions.
    const checkMidnight = () => {
      const now = new Date();
      if (now.getHours() === 0 && now.getMinutes() === 0) {
        localStorage.removeItem(`quranplus-daily-hadith-v2-${currentLang}`);
        fetchDailyHadith();
      }
    };
    const midnightInterval = setInterval(checkMidnight, 60000);
    return () => clearInterval(midnightInterval);
  }, [i18n.language]);

  // Share Verse of the Day — opens the VisualShareModal so the Ayat gets a
  // proper generated card (Arabic + translation + reference). Prior to
  // QA Bug C this called `navigator.share({ text })` only, which on Android
  // WebView typically fell through to plain-text share / clipboard and the
  // user never saw an image — the "Ayat shareable card not generating"
  // report from QA. Every other daily card (Story, Hadith, Du'a) already
  // routes through VisualShareModal; this brings Ayat in line.
  const shareVerse = useCallback(() => {
    if (!dailyVerse) {
      toast.error(t('share.noContent', 'No verse to share yet.'));
      return;
    }
    setVisualShareData({
      contentType: 'ayah',
      title: `${dailyVerse.surahName || `Surah ${dailyVerse.surah}`}`,
      arabicText: dailyVerse.text || '',
      translationText: dailyVerse.translation || '',
      transliterationText: '',
      reference: `${dailyVerse.surahName || `Surah ${dailyVerse.surah}`} · Ayah ${dailyVerse.ayah}`,
    });
    setShowVisualShare(true);
  }, [dailyVerse, t]);

  // Share Hadith of the Day - Opens Visual Share Modal for Image Generation
  const shareHadith = useCallback(async () => {
    if (!dailyHadith) return;
    
    // Open Visual Share Modal to generate image card
    setVisualShareData({
      contentType: 'hadith',
      title: 'Hadith of the Day',
      arabicText: dailyHadith.arabic || '',
      translationText: dailyHadith.text || '',
      transliterationText: '',
      reference: dailyHadith.source || dailyHadith.book || 'Sahih al-Bukhari',
    });
    setShowVisualShare(true);
  }, [dailyHadith]);

  const saveVerse = useCallback(() => {
    if (!dailyVerse) return;
    const bookmarks = JSON.parse(localStorage.getItem('quranplus-bookmarks') || '[]');
    const exists = bookmarks.some(b => b.surah === dailyVerse.surah && b.ayah === dailyVerse.ayah);
    if (!exists) {
      bookmarks.push({ ...dailyVerse, savedAt: new Date().toISOString() });
      localStorage.setItem('quranplus-bookmarks', JSON.stringify(bookmarks));
      toast.success('Verse saved to bookmarks!');
    } else {
      toast.info('Already bookmarked');
    }
  }, [dailyVerse]);

  // Pro Features data for auto-scrolling carousel - using translation keys
  // Each feature has a featureId for PRO gating via requestFeatureAccess
  const premiumFeatures = [
    { id: 'masjid-guide', icon: MapPin, titleKey: 'pro.masjidGuide', descKey: 'pro.masjidGuideDesc', path: '/mosques', featureId: 'mosque_finder', isPro: true },
    { id: 'masjid-connect', icon: Radio, titleKey: 'pro.mosqueSync', descKey: 'pro.mosqueSyncDesc', path: '/prayer-times#masjid-connect', featureId: null, isPro: false },
    { id: 'halal-scanner', icon: Scan, titleKey: 'pro.halalScanner', descKey: 'pro.halalScannerDesc', path: '/halal-scanner', featureId: 'halal_scanner', isPro: true },
    { id: 'zoom-quran', icon: Eye, titleKey: 'pro.zoomView', descKey: 'pro.zoomViewDesc', path: '/quran/zoom/1', featureId: 'zoom_mode', isPro: true },
    { id: 'dua-generator', icon: HandHeart, titleKey: 'pro.customDua', descKey: 'pro.customDuaDesc', path: '/duas', featureId: null, isPro: false },
    { id: 'smart-widget', icon: Smartphone, titleKey: 'pro.smartWidget', descKey: 'pro.smartWidgetDesc', path: '/settings#widget', featureId: 'smart_widget', isPro: true },
    { id: 'themes', icon: Palette, titleKey: 'pro.unlimitedThemes', descKey: 'pro.unlimitedThemesDesc', path: '/settings', featureId: 'all_themes', isPro: true },
    { id: 'offline', icon: Download, titleKey: 'pro.offlineMode', descKey: 'pro.offlineModeDesc', path: '/quran', featureId: null, isPro: false },
  ];

  // Pro Features (Emerald theme) — strict 8-item ordered list per user's brief.
  // Order: Tajweed Coach AI, Halal Scanner, Masjid Guide, Masjid Connect, Du'as, Smart Widgets, Zoom View, Premium Themes.
  const premiumFeaturesEmerald = [
    { id: 'tajweed-coach',  icon: Mic,        titleKey: 'pro.tajweedCoach',    descKey: 'pro.tajweedCoachDesc',    path: '/quran?tajweed=true',          featureId: 'tajweed_coach', isPro: true },
    { id: 'halal-scanner',  icon: Scan,       titleKey: 'pro.halalScanner',    descKey: 'pro.halalScannerDesc',    path: '/halal-scanner',               featureId: 'halal_scanner', isPro: true },
    { id: 'masjid-guide',   icon: MapPin,     titleKey: 'pro.masjidGuide',     descKey: 'pro.masjidGuideDesc',     path: '/mosques',                     featureId: 'mosque_finder', isPro: true },
    { id: 'masjid-connect', icon: Radio,      titleKey: 'pro.mosqueSync',      descKey: 'pro.mosqueSyncDesc',      path: '/prayer-times#masjid-connect', featureId: 'masjid_connect', isPro: true },
    { id: 'dua-generator',  icon: HandHeart,  titleKey: 'pro.customDua',       descKey: 'pro.customDuaDesc',       path: '/duas',                        featureId: 'dua_generator',  isPro: true },
    { id: 'smart-widget',   icon: Smartphone, titleKey: 'pro.smartWidget',     descKey: 'pro.smartWidgetDesc',     path: '/settings#widget',             featureId: 'smart_widget',  isPro: true },
    { id: 'zoom-quran',     icon: Eye,        titleKey: 'pro.zoomView',        descKey: 'pro.zoomViewDesc',        path: '/quran/zoom/1',                featureId: 'zoom_mode',     isPro: true },
    { id: 'themes',         icon: Palette,    titleKey: 'pro.unlimitedThemes', descKey: 'pro.unlimitedThemesDesc', path: '/settings',                    featureId: 'all_themes',    isPro: true },
  ];

  // Daily Dua data
  const [dailyDua, setDailyDua] = useState({
    arabic: 'اللَّهُمَّ إِنِّي أَسْأَلُكَ عِلْمًا نَافِعًا، وَرِزْقًا طَيِّبًا، وَعَمَلًا مُتَقَبَّلًا',
    translation: 'O Allah, I ask You for beneficial knowledge, good provision, and accepted deeds.',
    source: 'Ibn Majah',
  });

  // ============================================
  // ROYAL COLORS - Shared across all themes for language icon
  // ============================================
  const royalColors = isDark ? {
    // Night Mode - Dark navy cards on navy background
    pageBg: 'linear-gradient(180deg, #1a1a2e 0%, #16162a 50%, #0f0f1a 100%)',
    headerBg: 'linear-gradient(180deg, rgba(42, 42, 74, 0.6) 0%, rgba(26, 26, 46, 0.4) 100%)',
    headerBorder: 'rgba(201, 162, 39, 0.2)',
    cardBg: 'linear-gradient(180deg, #2a2a4a 0%, #1e1e3a 100%)',
    cardBorder: 'rgba(201, 162, 39, 0.3)',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255, 255, 255, 0.7)',
    textMuted: 'rgba(255, 255, 255, 0.5)',
    cardTextPrimary: '#ffffff',
    cardTextSecondary: 'rgba(255, 255, 255, 0.7)',
    gold: '#c9a227',
    goldMuted: 'rgba(201, 162, 39, 0.5)',
    purple: '#7c3aed',
    green: '#10b981',
    iconBg: 'linear-gradient(180deg, rgba(201, 162, 39, 0.15) 0%, rgba(201, 162, 39, 0.05) 100%)',
    featureBg: 'linear-gradient(180deg, #2a2a4a 0%, #1a1a2e 100%)',
    showStars: true,
  } : {
    // Day Mode - CREAM cards on navy background
    pageBg: 'linear-gradient(180deg, #1e2a4a 0%, #1a2540 50%, #141c32 100%)',
    headerBg: 'linear-gradient(180deg, rgba(30, 42, 74, 0.9) 0%, rgba(26, 37, 64, 0.95) 100%)',
    headerBorder: 'rgba(255, 255, 255, 0.8)',
    cardBg: 'linear-gradient(180deg, #fef6e3 0%, #fff8ed 100%)',
    cardBorder: '#c9a227',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255, 255, 255, 0.8)',
    textMuted: 'rgba(255, 255, 255, 0.6)',
    cardTextPrimary: '#000000',
    cardTextSecondary: '#333333',
    gold: '#c9a227',
    goldMuted: 'rgba(201, 162, 39, 0.5)',
    purple: '#7c3aed',
    green: '#10b981',
    iconBg: 'linear-gradient(180deg, rgba(201, 162, 39, 0.2) 0%, rgba(201, 162, 39, 0.1) 100%)',
    featureBg: 'linear-gradient(180deg, #fef6e3 0%, #fff8ed 100%)',
    showStars: false,
  };

  // ============================================
  // PREMIUM ROYAL THEME RENDER
  // ============================================
  if (isRoyal) {
    
    return (
      <div 
        className="min-h-screen pb-8 relative"
        style={{ background: royalColors.pageBg }}
        onClick={() => showLanguageMenu && setShowLanguageMenu(false)}
      >
        {/* Starry Background - Only for Night Mode */}
        {royalColors.showStars && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Stars layer */}
            <div className="absolute inset-0 opacity-50" style={{
              backgroundImage: `radial-gradient(1px 1px at 20px 30px, rgba(255,255,255,0.9) 0%, transparent 100%),
                                radial-gradient(1.5px 1.5px at 100px 150px, rgba(255,255,255,0.8) 0%, transparent 100%),
                                radial-gradient(1px 1px at 200px 80px, rgba(255,255,255,0.7) 0%, transparent 100%),
                                radial-gradient(1.5px 1.5px at 300px 200px, rgba(255,255,255,0.6) 0%, transparent 100%),
                                radial-gradient(1px 1px at 50px 250px, rgba(255,255,255,0.8) 0%, transparent 100%),
                                radial-gradient(2px 2px at 350px 100px, rgba(201,162,39,0.6) 0%, transparent 100%),
                                radial-gradient(1.5px 1.5px at 150px 350px, rgba(201,162,39,0.5) 0%, transparent 100%)`,
              backgroundSize: '400px 400px'
            }} />
            {/* Subtle golden ambient glow at top */}
            <div 
              className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px]"
              style={{
                background: 'radial-gradient(ellipse at center top, rgba(201, 162, 39, 0.08) 0%, transparent 70%)',
              }}
            />
          </div>
        )}
        
        {/* Day Mode subtle pattern */}
        {!royalColors.showStars && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div 
              className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px]"
              style={{
                background: 'radial-gradient(ellipse at center top, rgba(201, 162, 39, 0.08) 0%, transparent 60%)',
              }}
            />
          </div>
        )}
        
        <div className="relative max-w-lg lg:max-w-4xl xl:max-w-6xl mx-auto">
          {/* ============ PREMIUM HEADER - ELEVATED DESIGN ============ */}
          <div className="px-4 pt-6 pb-4">
            {/* Top bar with glassmorphism */}
            <div 
              className="flex items-center justify-between p-3 rounded-2xl"
              style={{
                background: royalColors.headerBg,
                backdropFilter: 'blur(20px)',
                border: isDark ? `1px solid ${royalColors.headerBorder}` : `2px solid rgba(255, 255, 255, 0.9)`,
                boxShadow: isDark 
                  ? '0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
                  : '0 8px 32px rgba(0, 0, 0, 0.15)',
              }}
            >
              {/* Logo & Tagline - More elegant */}
              <div className="flex items-center gap-3">
                <div 
                  className="relative p-2 rounded-xl"
                  style={{
                    background: royalColors.iconBg,
                    border: `1px solid ${royalColors.cardBorder}`,
                  }}
                >
                  <img 
                    src="/images/tanzeel-logo.png"
                    alt="T+" 
                    className="h-6 w-6 object-contain"
                  />
                </div>
                <div>
                  <h1 
                    className="text-lg font-bold tracking-wide" 
                    style={{ 
                      color: royalColors.gold,
                      fontFamily: "'Playfair Display', serif",
                    }}
                  >
                    Tanzeen<span style={{ color: royalColors.textSecondary }}>+</span>
                  </h1>
                  <p 
                    className="text-[8px] tracking-[0.12em] uppercase font-medium"
                    style={{ color: royalColors.textMuted }}
                  >
                    {t('app.tagline', 'Beyond Elegance')}
                  </p>
                </div>
              </div>
              
              {/* Header Icons - Moved more center + Hamburger inside header */}
              <div className="flex items-center gap-2">
                {/* Icons - Globe, Theme Toggle, Theme Selector */}
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLanguageMenu(!showLanguageMenu);
                  }}
                  className="p-2 rounded-full transition-all duration-300 hover:scale-110"
                  style={{
                    background: royalColors.iconBg,
                    border: `1px solid ${royalColors.cardBorder}`,
                  }}
                  data-testid="header-language-btn"
                >
                  <Globe 
                    className="w-3.5 h-3.5" 
                    style={{ color: royalColors.gold }}
                    strokeWidth={1.5} 
                  />
                </button>
                
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    const root = document.documentElement;
                    if (root.classList.contains('dark')) {
                      root.classList.remove('dark');
                      root.classList.add('light');
                      localStorage.setItem('noor-mode', 'light');
                    } else {
                      root.classList.remove('light');
                      root.classList.add('dark');
                      localStorage.setItem('noor-mode', 'dark');
                    }
                    window.location.reload();
                  }}
                  className="p-2 rounded-full transition-all duration-300 hover:scale-110"
                  style={{
                    background: royalColors.iconBg,
                    border: `1px solid ${royalColors.cardBorder}`,
                  }}
                  data-testid="header-theme-btn"
                >
                  {isDark ? (
                    <Moon 
                      className="w-3.5 h-3.5" 
                      style={{ color: royalColors.gold }}
                      strokeWidth={1.5} 
                    />
                  ) : (
                    <Sun 
                      className="w-3.5 h-3.5" 
                      style={{ color: royalColors.gold }}
                      strokeWidth={1.5} 
                    />
                  )}
                </button>
                
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setThemeDialogOpen(true);
                  }}
                  className="p-2 rounded-full transition-all duration-300 hover:scale-110"
                  style={{
                    background: royalColors.iconBg,
                    border: `1px solid ${royalColors.cardBorder}`,
                  }}
                  data-testid="header-palette-btn"
                >
                  <Palette 
                    className="w-3.5 h-3.5" 
                    style={{ color: royalColors.gold }}
                    strokeWidth={1.5} 
                  />
                </button>
                
                {/* Hamburger Menu - Inside header on right */}
                <Sheet>
                  <SheetTrigger asChild>
                    <button 
                      className="p-2.5 rounded-xl transition-all duration-300 hover:scale-105 lg:hidden"
                      style={{
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)',
                      }}
                      data-testid="royal-home-hamburger"
                    >
                      <Menu className="w-5 h-5 text-white" />
                    </button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-80 p-0 border-0 !bg-transparent [&>button]:text-white [&>button]:opacity-100" style={{ background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)' }}>
                    <div className="flex h-full flex-col">
                      {/* Menu Header - Royal Knight Elegant */}
                      <div className="p-6" style={{ background: 'linear-gradient(135deg, #2a2a4a 0%, #1e1e3a 100%)', borderBottom: '2px solid rgba(201, 162, 39, 0.4)' }}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <img src="/images/tanzeel-logo.png" alt="Tanzeen +" className="h-10 w-10 object-contain" />
                            <div>
                              <div style={{ color: '#ffffff', fontSize: '1.25rem', fontWeight: 700, fontFamily: 'serif', letterSpacing: '0.025em', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                                Tanzeen <span style={{ color: '#c9a227', fontWeight: 900 }}>+</span>
                              </div>
                              <div style={{ color: '#ffffff', fontSize: '10px', fontStyle: 'italic', letterSpacing: '0.05em', opacity: 0.9 }}>{t('app.tagline')}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Navigation Items */}
                      <ScrollArea className="flex-1 py-4">
                        <nav className="space-y-2 px-4">
                          {/* SYNCED with PageHeader.jsx (14 items, correct order) */}
                          {[
                            { path: '/', label: t('nav.home', 'Home'), icon: Home },
                            { path: '/quran', label: t('nav.quran'), icon: BookOpen },
                            { path: '/prayer-times', label: t('nav.prayerTimes'), icon: Clock },
                            { path: '/duas', label: t('nav.duas', 'Duas'), icon: HandHeart },
                            { path: '/hadith', label: t('nav.hadith', 'Hadith'), icon: BookText },
                            { path: '/mosques', label: t('nav.masjidGuide', 'Masjid Guide'), icon: MapPin, isPro: true, featureId: 'mosque_finder' },
                            { path: '/prayer-times#masjid-connect', label: t('nav.masjidConnect', 'Masjid Connect'), icon: Radio, isPro: true, featureId: 'masjid_connect' },
                            { path: '/halal-scanner', label: t('nav.halalScanner'), icon: Scan, isPro: true, featureId: 'halal_scanner' },
                            { path: '/progress', label: t('nav.progress', 'Progress'), icon: TrendingUp, isPro: true, featureId: 'reading_stats' },
                            { path: '/quran/zoom/1', label: t('nav.zoom', 'Zoom View'), icon: ZoomIn, isPro: true, featureId: 'zoom_mode' },
                            { path: '/qibla', label: t('nav.qibla'), icon: Compass },
                            { path: '/bookmarks', label: t('nav.saved'), icon: Bookmark },
                            // Search hidden as per user request
                            { path: '/settings', label: t('nav.settings'), icon: Settings },
                          ].map((item) => {
                            const Icon = item.icon;
                            const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
                            const isActive = routerLocation.pathname === item.path || 
                              (item.path === '/quran' && routerLocation.pathname.startsWith('/quran'));
                            
                            // Elegant Royal Knight nav item styling with cream backgrounds
                            const navItemStyle = isActive 
                              ? {
                                  background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #7c3aed 100%)',
                                  boxShadow: '0 4px 20px rgba(139, 92, 246, 0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
                                  border: '1px solid rgba(139, 92, 246, 0.5)',
                                }
                              : {
                                  background: 'linear-gradient(135deg, #f5f0e6 0%, #ebe5d9 100%)',
                                  border: '1px solid rgba(201, 162, 39, 0.4)',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                };
                            
                            // Icon container styling - gold outline on cream
                            const iconContainerStyle = isActive 
                              ? {
                                  background: 'rgba(255, 255, 255, 0.2)',
                                  border: '2px solid rgba(255, 255, 255, 0.4)',
                                }
                              : {
                                  background: 'rgba(201, 162, 39, 0.2)',
                                  border: '2px solid #b8860b',
                                };
                            
                            // Text and icon colors - dark text on cream, green icons, white on active
                            const textColor = isActive ? '#ffffff' : '#2d2a26';
                            const iconColor = isActive ? '#ffffff' : '#10b981';
                            
                            return (
                              <Link
                                key={item.path}
                                to={isItemLocked ? '#' : item.path}
                                onClick={(e) => {
                                  if (isItemLocked) {
                                    e.preventDefault();
                                    promptUpgrade(item.featureId);
                                  }
                                }}
                                className="flex items-center gap-4 rounded-2xl px-4 py-4 transition-all duration-300 no-underline"
                                style={{...navItemStyle, textDecoration: 'none'}}
                              >
                                <div 
                                  className="rounded-xl p-2.5 flex-shrink-0"
                                  style={iconContainerStyle}
                                >
                                  <Icon 
                                    className="h-5 w-5" 
                                    style={{ color: iconColor, stroke: iconColor }} 
                                    strokeWidth={2}
                                  />
                                </div>
                                <span 
                                  className="flex-1 font-semibold text-base tracking-wide"
                                  data-testid="royal-nav-label"
                                  style={{ 
                                    color: textColor,
                                    WebkitTextFillColor: textColor,
                                    textShadow: isActive ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
                                  }}
                                >
                                  {item.label}
                                </span>
                                {isItemLocked && (
                                  <span 
                                    className="px-2 py-1 rounded-lg text-[9px] font-bold flex items-center gap-1 flex-shrink-0"
                                    style={{
                                      background: 'linear-gradient(135deg, #c9a227 0%, #d4af37 100%)',
                                      color: '#0f0f1a',
                                      boxShadow: '0 2px 8px rgba(201, 162, 39, 0.3)',
                                    }}
                                  >
                                    <Crown className="h-2.5 w-2.5" />
                                    PRO
                                  </span>
                                )}
                              </Link>
                            );
                          })}
                        </nav>
                      </ScrollArea>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
            
            {/* Language Dropdown Menu - ROYAL is DARK theme, so dropdown should be LIGHT/BRIGHT */}
            {showLanguageMenu && (
              <div 
                dir="ltr"
                className="absolute right-20 top-16 z-[100] w-52 rounded-2xl max-h-72 overflow-y-auto overscroll-contain"
                style={{
                  background: 'linear-gradient(180deg, #ffffff 0%, #fef9f0 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25), 0 0 30px rgba(201, 162, 39, 0.15)',
                  WebkitOverflowScrolling: 'touch',
                }}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
              >
                <div className="p-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setShowLanguageMenu(false);
                        toast.success(`Language changed to ${lang.name}`);
                      }}
                      className={`w-full px-3 py-2.5 text-left rounded-xl text-sm flex items-center gap-3 transition-all duration-200 ${
                        i18n.language === lang.code 
                          ? 'bg-amber-100'
                          : 'hover:bg-amber-50'
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span style={{ color: i18n.language === lang.code ? '#B8860B' : '#1a1a1a' }} className={i18n.language === lang.code ? 'font-medium' : ''}>{lang.name}</span>
                      {i18n.language === lang.code && <Check className="w-4 h-4 ml-auto" style={{ color: '#B8860B' }} />}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Premium Prayer Time Card */}
            <Link 
              to="/prayer-times" 
              className="mt-4 flex items-center justify-between px-4 py-3 rounded-2xl transition-all duration-300 hover:scale-[1.02]"
              style={{
                background: isDark ? royalColors.cardBg : royalColors.pageBg,
                border: isDark ? `1px solid ${royalColors.cardBorder}` : `2px solid ${royalColors.green}`,
                boxShadow: isDark ? '0 4px 20px rgba(0, 0, 0, 0.15)' : '0 4px 20px rgba(16, 185, 129, 0.15)',
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${royalColors.green} 0%, #059669 100%)`,
                  }}
                >
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium tracking-wide uppercase" style={{ color: royalColors.textMuted }}>{t('home.nextPrayer', "Next Prayer")}</p>
                  <p className="font-semibold" style={{ color: royalColors.textPrimary }}>{nextPrayer?.name || 'Loading'}</p>
                </div>
              </div>
              <div className="text-right">
                <p 
                  className="text-lg font-bold" 
                  style={{ color: isDark ? royalColors.gold : royalColors.textPrimary }}
                >
                  {nextPrayer ? formatTime12h(nextPrayer.time) : '--:--'}
                </p>
                <p className="text-xs font-medium" style={{ color: royalColors.textMuted }}>in {countdown.hours}h {countdown.minutes}m</p>
              </div>
            </Link>
          </div>
          
          {/* ============ CORE FEATURES ============ */}
          <OrnateDivider title={t('home.coreFeatures', 'Core Features')} />
          
          {/* Premium Core Features - Font Awesome Icons */}
          <div className="px-4 py-2">
            <div className="flex justify-around items-center">
              {[
                { icon: 'book-quran', label: t('nav.quran', 'Quran'), path: '/quran' },
                { icon: 'hands-praying', label: t('nav.duas', 'Duas'), path: '/duas' },
                { icon: 'compass', label: t('nav.qibla', 'Qibla'), path: '/qibla' },
                { icon: 'book-open-reader', label: t('nav.hadith', 'Hadiths'), path: '/hadith' },
                { icon: 'clock', label: t('nav.prayer', 'Prayer'), path: '/prayer-times' },
              ].map((item, idx) => (
                <Link 
                  key={idx}
                  to={item.path}
                  className="flex-shrink-0 flex flex-col items-center gap-3 group"
                >
                  {/* Original size circle with premium Font Awesome icon inside */}
                  <div 
                    className="relative p-[3px] rounded-full transition-all duration-300 group-hover:scale-110"
                    style={{
                      background: 'linear-gradient(180deg, #c9a227 0%, #b8860b 50%, #c9a227 100%)',
                      boxShadow: '0 0 24px rgba(201, 162, 39, 0.5), 0 4px 16px rgba(0, 0, 0, 0.3)',
                    }}
                  >
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center relative overflow-hidden"
                      style={{ 
                        background: 'linear-gradient(145deg, #fffbeb 0%, #fef3c7 100%)',
                      }}
                    >
                      {/* Subtle inner highlight */}
                      <div 
                        className="absolute inset-0 rounded-full"
                        style={{
                          background: 'radial-gradient(circle at 35% 35%, rgba(255, 255, 255, 0.6) 0%, transparent 50%)',
                        }}
                      />
                      {/* Font Awesome Icon */}
                      <FontAwesomeIcon 
                        icon={['fas', item.icon]}
                        className="text-2xl relative z-10 transition-all duration-300 group-hover:scale-110"
                        style={{ 
                          color: '#5b21b6',
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Elegant gold label */}
                  <span 
                    className="text-xs font-semibold tracking-wider uppercase transition-all duration-300 group-hover:text-yellow-300"
                    style={{ 
                      color: '#c9a227',
                      textShadow: '0 0 12px rgba(201, 162, 39, 0.4)',
                      letterSpacing: '0.1em',
                    }}
                  >
                    {item.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>
          
          {/* Tajweed Coach Hero Card - Flagship Feature */}
          <div className="px-4 mt-4">
            <MagicCoachHeroCard />
          </div>
          
          <div className="px-4">
            {/* ============ PRO FEATURES AUTO-SCROLL CAROUSEL ============ */}
            <OrnateDivider title={t('home.proFeatures', 'Pro Features')} />
            
            <div className="mt-3">
              <AutoScrollCarousel speed={60} direction="left">
              {premiumFeatures.map((feature) => {
                // Check if this feature requires PRO and user doesn't have access
                const needsProGate = feature.isPro && !isPro && !(feature.featureId && isFeatureUnlockedViaAd(feature.featureId));
                
                const handleFeatureClick = () => {
                  if (needsProGate) {
                    // Show PRO modal instead of navigating
                    requestFeatureAccess(
                      { id: feature.featureId, name: t(feature.titleKey, feature.titleKey.split('.').pop()) },
                      () => navigate(feature.path)
                    );
                  } else {
                    navigate(feature.path);
                  }
                };
                
                return (
                <div key={feature.id} className="flex-shrink-0 w-72">
                  {/* Premium Feature Card - Cream in Day, Dark in Night */}
                  <div 
                    className="relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:scale-[1.03] group"
                    onClick={handleFeatureClick}
                    data-testid={`pro-feature-card-${feature.id}`}
                    style={{
                      background: royalColors.cardBg,
                      border: `2px solid ${royalColors.cardBorder}`,
                      boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 4px 20px rgba(201, 162, 39, 0.15)',
                    }}
                  >
                    {/* Corner flourishes for Day mode */}
                    {!isDark && (
                      <>
                        <div className="absolute top-0 left-0 w-4 h-4 border-b border-r border-[#c9a227]/40 rounded-br-xl" />
                        <div className="absolute top-0 right-0 w-4 h-4 border-b border-l border-[#c9a227]/40 rounded-bl-xl" />
                        <div className="absolute bottom-0 left-0 w-4 h-4 border-t border-r border-[#c9a227]/40 rounded-tr-xl" />
                        <div className="absolute bottom-0 right-0 w-4 h-4 border-t border-l border-[#c9a227]/40 rounded-tl-xl" />
                      </>
                    )}
                    
                    {/* Pro badge - only show for PRO features when locked */}
                    {feature.isPro && (
                    <div 
                      className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider flex items-center gap-1"
                      style={{
                        background: needsProGate 
                          ? 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)' 
                          : `linear-gradient(135deg, ${royalColors.purple} 0%, #5b21b6 100%)`,
                        color: '#ffffff',
                        boxShadow: needsProGate ? '0 2px 10px rgba(245, 158, 11, 0.3)' : '0 2px 10px rgba(124, 58, 237, 0.3)',
                      }}
                    >
                      {needsProGate && <Lock className="w-2.5 h-2.5" />}
                      PRO
                    </div>
                    )}
                    
                    <div className="p-4 relative">
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{
                            background: `linear-gradient(135deg, ${royalColors.purple} 0%, #5b21b6 100%)`,
                            boxShadow: '0 2px 10px rgba(124, 58, 237, 0.3)',
                          }}
                        >
                          <feature.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold mb-0.5" data-premium-card="title" style={{ color: royalColors.cardTextPrimary }}>{t(feature.titleKey, feature.titleKey.split('.').pop())}</h3>
                          <p className="text-xs leading-snug font-medium" data-premium-card="desc" style={{ color: royalColors.cardTextSecondary }}>{t(feature.descKey, feature.descKey.split('.').pop())}</p>
                        </div>
                        
                        <ChevronRight className="w-5 h-5 flex-shrink-0 group-hover:translate-x-1 transition-all duration-300" style={{ color: royalColors.gold }} />
                      </div>
                    </div>
                  </div>
                </div>
              );
              })}
            </AutoScrollCarousel>
            </div>
            
            {/* ============ TODAY'S STORY ============ */}
            <OrnateDivider title={t('home.todaysStory', "Today's Story")} />
            
            <div className="mt-3 mb-4">
              <TodaysStoryCard 
                navigate={navigate} 
                t={t}
                currentLang={i18n.language}
                onShare={openStoryShare}
                themeColors={{
                  border: royalColors.cardBorder,
                  headerBg: `linear-gradient(135deg, ${royalColors.green} 0%, #059669 100%)`,
                  headerAccent: '#ffffff',
                  cardBg: isDark ? '#2a2a4a' : '#fef6e3',
                  titleColor: isDark ? '#ffffff' : '#000000',
                  previewColor: isDark ? 'rgba(255,255,255,0.8)' : '#333333',
                  linkColor: royalColors.gold,
                  isDark: isDark,
                }}
              />
            </div>
            
            {/* ============ RECITERS SECTION ============ */}
            <OrnateDivider title={t('home.reciters', 'Reciters')} />
            
            <div className="mt-3">
              <div className="flex gap-4 overflow-x-auto pb-4 pt-2 scrollbar-hide items-start" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
              <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>
              {RECITERS.map((reciter) => {
                const isLocked = !reciter.isFree; // Always show PRO for non-free reciters
                return (
                <div 
                  key={reciter.id}
                  className="flex-shrink-0 w-24 cursor-pointer transition-all duration-300 hover:scale-105 text-center group relative"
                  onClick={() => {
                    // Navigate to settings page reciters section
                    navigate('/settings#reciters');
                  }}
                >
                  {/* Always show PRO badge for non-free reciters */}
                  {!reciter.isFree && (
                    <div className="absolute -top-2 -right-2 z-50 px-1.5 py-0.5 rounded-full bg-orange-500 text-white text-[8px] font-bold shadow-lg flex items-center gap-0.5" style={{ minWidth: '32px' }}>
                      <Lock className="w-2.5 h-2.5" />
                      PRO
                    </div>
                  )}
                  {/* Always show FREE badge for free reciter */}
                  {reciter.isFree && (
                    <div className="absolute -top-2 -right-2 z-50 px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-[8px] font-bold shadow-lg">
                      FREE
                    </div>
                  )}
                  {/* Circular reciter photo with gold outline */}
                  <div 
                    className={`w-16 h-16 mx-auto rounded-full overflow-hidden transition-all duration-300 ${isLocked ? 'opacity-60' : ''}`}
                    style={{
                      border: `3px solid ${royalColors.gold}`,
                      boxShadow: isDark ? '0 4px 15px rgba(0, 0, 0, 0.3), 0 0 10px rgba(201, 162, 39, 0.3)' : '0 4px 15px rgba(201, 162, 39, 0.3), 0 0 10px rgba(201, 162, 39, 0.2)',
                    }}
                  >
                    <img 
                      src={reciter.image} 
                      alt={reciter.fullName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="mt-2">
                    <p className={`font-semibold text-xs truncate ${isLocked ? 'opacity-60' : ''}`} style={{ color: royalColors.textPrimary }}>{reciter.name}</p>
                    <p className={`text-[10px] truncate ${isLocked ? 'opacity-60' : ''}`} style={{ color: royalColors.textMuted }}>{reciter.country}</p>
                  </div>
                </div>
              )})}
              {/* More button */}
              <div 
                className="flex-shrink-0 w-20 cursor-pointer transition-all duration-300 hover:scale-105 flex flex-col items-center justify-center"
                onClick={() => navigate('/settings#reciters')}
              >
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{
                    border: `2px dashed ${royalColors.gold}`,
                    background: isDark ? 'rgba(201, 162, 39, 0.1)' : 'rgba(201, 162, 39, 0.05)',
                  }}
                >
                  <ChevronRight className="w-6 h-6" style={{ color: royalColors.gold }} />
                </div>
                <p className="mt-2 font-semibold text-xs" style={{ color: royalColors.gold }}>More</p>
              </div>
              </div>
            </div>
            
            {/* ============ HADITH OF THE DAY ============ */}
            <OrnateDivider title={t('home.hadithOfDay', 'Hadith of the Day')} />
            
            <div 
              className="relative rounded-2xl overflow-hidden mt-3 mb-4"
              style={{
                background: royalColors.cardBg,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${royalColors.cardBorder}`,
                boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(201, 162, 39, 0.1)',
              }}
            >
              {/* Premium corner accents */}
              <div className="absolute top-0 left-0 w-8 h-8">
                <div className="absolute top-0 left-0 w-full h-[1px]" style={{ background: `linear-gradient(to right, ${royalColors.gold}, transparent)` }} />
                <div className="absolute top-0 left-0 h-full w-[1px]" style={{ background: `linear-gradient(to bottom, ${royalColors.gold}, transparent)` }} />
              </div>
              <div className="absolute top-0 right-0 w-8 h-8">
                <div className="absolute top-0 right-0 w-full h-[1px]" style={{ background: `linear-gradient(to left, ${royalColors.gold}, transparent)` }} />
                <div className="absolute top-0 right-0 h-full w-[1px]" style={{ background: `linear-gradient(to bottom, ${royalColors.gold}, transparent)` }} />
              </div>
              <div className="absolute bottom-0 left-0 w-8 h-8">
                <div className="absolute bottom-0 left-0 w-full h-[1px]" style={{ background: `linear-gradient(to right, ${royalColors.gold}, transparent)` }} />
                <div className="absolute bottom-0 left-0 h-full w-[1px]" style={{ background: `linear-gradient(to top, ${royalColors.gold}, transparent)` }} />
              </div>
              <div className="absolute bottom-0 right-0 w-8 h-8">
                <div className="absolute bottom-0 right-0 w-full h-[1px]" style={{ background: `linear-gradient(to left, ${royalColors.gold}, transparent)` }} />
                <div className="absolute bottom-0 right-0 h-full w-[1px]" style={{ background: `linear-gradient(to top, ${royalColors.gold}, transparent)` }} />
              </div>
              
              <div className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${royalColors.purple} 0%, #5b21b6 100%)`,
                      boxShadow: '0 2px 10px rgba(124, 58, 237, 0.3)',
                    }}
                  >
                    <BookMarked className="w-5 h-5 text-white" />
                  </div>
                  <span 
                    className="font-bold" 
                    style={{ color: royalColors.cardTextPrimary, fontFamily: "'Playfair Display', serif" }}
                  >
                    {t('home.hadithOfDay', 'Hadith of the Day')}
                  </span>
                  {/* Share Button */}
                  <button
                    onClick={shareHadith}
                    className="ml-auto p-2 rounded-full transition-all hover:scale-110"
                    style={{ background: `${royalColors.gold}20` }}
                    data-testid="share-hadith-royal"
                  >
                    <Share2 className="w-4 h-4" style={{ color: royalColors.gold }} />
                  </button>
                  <span className="text-xs font-medium" style={{ color: royalColors.cardTextSecondary }}>{dailyHadith?.book}</span>
                </div>
                
                {dailyHadith?.text && (
                  <p className="text-sm leading-relaxed" style={{ color: royalColors.cardTextPrimary }}>"{dailyHadith.text}"</p>
                )}
                
                <div className="h-[1px] my-4" style={{ background: `linear-gradient(to right, transparent, ${royalColors.gold}, transparent)` }} />
                
                <Link to="/hadith" className="text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all" style={{ color: isDark ? royalColors.gold : royalColors.purple }}>
                  {t('home.readMoreHadiths', 'Read More Hadiths')} <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            
            
            {/* ============ QUICK STATS ============ */}
            <OrnateDivider title={t('home.quickStats', 'Quick Stats')} />
            
            {/* Premium Quick Stats Card */}
            <div 
              className="relative rounded-2xl overflow-hidden cursor-pointer transition-transform hover:scale-[1.02] mt-3"
              onClick={() => isPro ? navigate('/progress') : promptUpgrade('progress')}
              style={{
                background: royalColors.cardBg,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${royalColors.cardBorder}`,
                boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(201, 162, 39, 0.1)',
              }}
              data-testid="quick-stats-card"
            >
              {/* PRO Lock Badge */}
              <div 
                className="absolute top-3 right-3 px-2 py-1 rounded-full flex items-center gap-1 z-10"
                style={{
                  background: `linear-gradient(135deg, ${royalColors.purple} 0%, #5b21b6 100%)`,
                  boxShadow: '0 2px 10px rgba(124, 58, 237, 0.3)',
                }}
              >
                <Lock className="w-3 h-3 text-white" strokeWidth={2} />
                <span className="text-[9px] font-bold text-white tracking-wider">PRO</span>
              </div>
              
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-6 h-6">
                <div className="absolute top-0 left-0 w-full h-[1px]" style={{ background: `linear-gradient(to right, ${royalColors.gold}, transparent)` }} />
                <div className="absolute top-0 left-0 h-full w-[1px]" style={{ background: `linear-gradient(to bottom, ${royalColors.gold}, transparent)` }} />
              </div>
              <div className="absolute top-0 right-0 w-6 h-6">
                <div className="absolute top-0 right-0 w-full h-[1px]" style={{ background: `linear-gradient(to left, ${royalColors.gold}, transparent)` }} />
                <div className="absolute top-0 right-0 h-full w-[1px]" style={{ background: `linear-gradient(to bottom, ${royalColors.gold}, transparent)` }} />
              </div>
              
              <div className="p-5">
                <div className="grid grid-cols-3 gap-4">
                  {/* Day Streak */}
                  <div className="text-center">
                    <div 
                      className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-2"
                      style={{
                        background: `linear-gradient(135deg, ${royalColors.purple} 0%, #5b21b6 100%)`,
                        boxShadow: '0 2px 10px rgba(124, 58, 237, 0.3)',
                      }}
                    >
                      <Flame className="w-7 h-7 text-white" strokeWidth={1.5} />
                    </div>
                    <p className="text-2xl font-bold" style={{ color: royalColors.cardTextPrimary }}>{stats.streak}</p>
                    <p className="text-xs" style={{ color: royalColors.cardTextSecondary }}>{t('stats.dayStreak', 'Day Streak')}</p>
                  </div>
                  
                  {/* Total Time */}
                  <div className="text-center">
                    <div 
                      className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-2"
                      style={{
                        background: `linear-gradient(135deg, ${royalColors.purple} 0%, #5b21b6 100%)`,
                        boxShadow: '0 2px 10px rgba(124, 58, 237, 0.3)',
                      }}
                    >
                      <Clock className="w-7 h-7 text-white" strokeWidth={1.5} />
                    </div>
                    <p className="text-2xl font-bold" style={{ color: royalColors.cardTextPrimary }}>{stats.totalTime}</p>
                    <p className="text-xs" style={{ color: royalColors.cardTextSecondary }}>{t('stats.totalTime', 'Total Time')}</p>
                  </div>
                  
                  {/* Surahs Completed */}
                  <div className="text-center">
                    <div 
                      className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-2"
                      style={{
                        background: `linear-gradient(135deg, ${royalColors.purple} 0%, #5b21b6 100%)`,
                        boxShadow: '0 2px 10px rgba(124, 58, 237, 0.3)',
                      }}
                    >
                      <BookOpen className="w-7 h-7 text-white" strokeWidth={1.5} />
                    </div>
                    <p className="text-2xl font-bold" style={{ color: royalColors.cardTextPrimary }}>{stats.surahs}/114</p>
                    <p className="text-xs" style={{ color: royalColors.cardTextSecondary }}>{t('stats.surahs', 'Surahs')}</p>
                  </div>
                </div>
                
                {/* Tap to view more */}
                <div className="flex items-center justify-center gap-1 mt-4 text-xs" style={{ color: royalColors.cardTextSecondary }}>
                  <span>{t('stats.tapToView', 'Tap to view progress')}</span>
                  <ChevronRight className="w-3 h-3" />
                </div>
              </div>
            </div>
            
            {/* ============ PRO UPGRADE CARD ============ */}
            <div 
              className="relative rounded-2xl overflow-hidden mt-6"
              style={{
                background: royalColors.cardBg,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${royalColors.cardBorder}`,
                boxShadow: isDark ? '0 8px 40px rgba(0, 0, 0, 0.4), 0 0 60px rgba(201, 162, 39, 0.1)' : '0 8px 40px rgba(201, 162, 39, 0.15)',
              }}
            >
              {/* Premium corner accents */}
              <div className="absolute top-0 left-0 w-12 h-12">
                <div className="absolute top-0 left-0 w-full h-[2px]" style={{ background: `linear-gradient(to right, ${royalColors.gold}, transparent)` }} />
                <div className="absolute top-0 left-0 h-full w-[2px]" style={{ background: `linear-gradient(to bottom, ${royalColors.gold}, transparent)` }} />
              </div>
              <div className="absolute top-0 right-0 w-12 h-12">
                <div className="absolute top-0 right-0 w-full h-[2px]" style={{ background: `linear-gradient(to left, ${royalColors.gold}, transparent)` }} />
                <div className="absolute top-0 right-0 h-full w-[2px]" style={{ background: `linear-gradient(to bottom, ${royalColors.gold}, transparent)` }} />
              </div>
              <div className="absolute bottom-0 left-0 w-12 h-12">
                <div className="absolute bottom-0 left-0 w-full h-[2px]" style={{ background: `linear-gradient(to right, ${royalColors.gold}, transparent)` }} />
                <div className="absolute bottom-0 left-0 h-full w-[2px]" style={{ background: `linear-gradient(to top, ${royalColors.gold}, transparent)` }} />
              </div>
              <div className="absolute bottom-0 right-0 w-12 h-12">
                <div className="absolute bottom-0 right-0 w-full h-[2px]" style={{ background: `linear-gradient(to left, ${royalColors.gold}, transparent)` }} />
                <div className="absolute bottom-0 right-0 h-full w-[2px]" style={{ background: `linear-gradient(to top, ${royalColors.gold}, transparent)` }} />
              </div>
              
              {/* Header Section - Premium Golden */}
              <div 
                className="p-5 flex items-center justify-center relative overflow-hidden"
                style={{
                  background: royalColors.iconBg,
                }}
              >
                {/* Subtle glow */}
                <div 
                  className="absolute inset-0"
                  style={{
                    background: `radial-gradient(ellipse at center, ${royalColors.goldMuted} 0%, transparent 70%)`,
                    opacity: 0.3,
                  }}
                />
                <div className="flex items-center gap-4 relative">
                  <div 
                    className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{
                      background: `linear-gradient(180deg, ${royalColors.gold} 0%, #a88b1e 100%)`,
                      boxShadow: `0 8px 30px ${royalColors.goldMuted}`,
                    }}
                  >
                    <Crown className="w-7 h-7" style={{ color: isDark ? '#1a1a2e' : '#ffffff' }} strokeWidth={2} />
                  </div>
                  <div>
                    <h3 
                      className="font-bold text-xl" 
                      data-premium-card="title"
                      style={{ 
                        color: isDark ? royalColors.gold : '#000000',
                        textShadow: isDark ? '0 0 20px rgba(201, 162, 39, 0.4)' : 'none',
                        fontFamily: "'Playfair Display', serif",
                      }}
                    >
                      Tanzeen+ Pro
                    </h3>
                    <p className="text-sm" data-premium-card="desc" style={{ color: isDark ? royalColors.textMuted : '#333333' }}>{t('pro.upgradeAccess', 'Upgrade for full access')}</p>
                  </div>
                </div>
              </div>
              
              {/* Features Grid */}
              <div className="p-5">
                <div className="grid grid-cols-2 gap-3 mb-5">
                  {[
                    { icon: Eye, label: t('pro.zoomView', 'Zoom View Mode') },
                    { icon: Scan, label: t('pro.halalScanner', 'Halal Scanner') },
                    { icon: Smartphone, label: t('pro.smartWidget', 'Smart Widget') },
                    { icon: HandHeart, label: t('pro.customDua', 'Custom Du\'a Generator') },
                    { icon: MapPin, label: t('pro.masjidGuide', 'Masjid Guide') },
                    { icon: Volume2, label: t('pro.reciters', 'All Reciters') },
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: isDark ? royalColors.iconBg : 'rgba(124, 58, 237, 0.1)' }}>
                      <feature.icon className="w-4 h-4" style={{ color: royalColors.purple }} strokeWidth={1.5} />
                      <span className="text-sm font-medium" style={{ color: royalColors.cardTextPrimary }}>{feature.label}</span>
                    </div>
                  ))}
                </div>
                
                {/* Unlock Button */}
                <button
                  onClick={() => promptUpgrade(null)}
                  className="w-full py-4 rounded-xl flex items-center justify-center gap-3 font-bold text-lg transition-all duration-300 hover:scale-[1.02]"
                  style={{
                    background: `linear-gradient(135deg, ${royalColors.gold} 0%, #a88b1e 100%)`,
                    boxShadow: '0 4px 15px rgba(201, 162, 39, 0.3)',
                    color: '#1a1a2e',
                  }}
                  data-testid="unlock-premium-bottom-btn"
                >
                  <Sparkles className="w-5 h-5" />
                  {t('pro.unlock', 'Unlock Premium')}
                </button>
                
                {/* Secure Payment */}
                <p className="text-center text-xs mt-4" style={{ color: royalColors.cardTextSecondary }}>
                  {t('pro.securePayment', 'Secure payment via App Store / Google Play')}
                </p>
                
                {/* Restore Purchases */}
                <button 
                  className="w-full mt-3 py-3 rounded-xl flex items-center justify-center gap-2 font-medium transition-all"
                  style={{
                    border: `2px solid ${isDark ? 'rgba(255,255,255,0.1)' : royalColors.gold}`,
                    color: royalColors.cardTextPrimary,
                  }}
                >
                  <ArrowRight className="w-4 h-4 rotate-180" />
                  {t('pro.restore', 'Restore Purchases')}
                </button>
              </div>
            </div>
            
          </div>
        </div>
        
        {/* Visual Share Modal - Royal Theme */}
        {visualShareData && (
          <VisualShareModal
            isOpen={showVisualShare}
            onClose={() => {
              setShowVisualShare(false);
              setVisualShareData(null);
            }}
            contentType={visualShareData.contentType}
            title={visualShareData.title}
            arabicText={visualShareData.arabicText}
            translationText={visualShareData.translationText}
            transliterationText={visualShareData.transliterationText}
            reference={visualShareData.reference}
          />
        )}
        
        {/* Theme Selector Dialog - Royal Theme - Matching QuranPage Design */}
        <Dialog open={themeDialogOpen} onOpenChange={setThemeDialogOpen}>
          <DialogContent className="sm:max-w-md" style={{ background: '#ffffff', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
            <DialogHeader>
              <DialogTitle className="text-center font-serif text-xl" style={{ color: '#B8860B' }}>
                {t('settings.selectTheme', 'Select Theme')}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-4">
              {Object.entries(APP_THEMES).map(([key, theme]) => {
                const isSelected = appTheme === key;
                const isFreeTheme = key === 'classic';
                const isLocked = !isPro && !isFreeTheme;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      if (isLocked) {
                        requestFeatureAccess({ 
                          id: 'all_themes', 
                          name: 'Premium Themes',
                          noAdUnlock: true 
                        }, () => {
                          setAppTheme(key);
                          setThemeDialogOpen(false);
                        });
                      } else {
                        setAppTheme(key);
                        setThemeDialogOpen(false);
                      }
                    }}
                    data-testid={`theme-option-${key}`}
                    className={`relative text-left rounded-xl border-2 overflow-hidden transition-all ${
                      isSelected
                        ? 'border-amber-500 shadow-lg ring-2 ring-amber-500/20'
                        : isLocked 
                          ? 'border-gray-200 opacity-70'
                          : 'border-gray-200 hover:border-amber-500/50'
                    }`}
                  >
                    {/* Color preview bars */}
                    <div className="h-12 flex">
                      <div className="flex-1" style={{ backgroundColor: theme.preview?.background || '#1a1a1a' }} />
                      <div className="w-4" style={{ backgroundColor: theme.preview?.primary || '#333' }} />
                      <div className="w-4" style={{ backgroundColor: theme.preview?.secondary || '#666' }} />
                    </div>
                    {/* Theme name section - cream background */}
                    <div className="p-2 bg-[#f5f0e6]">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-semibold text-black">{theme.name}</p>
                          <p className="text-[10px] text-gray-700">{theme.description}</p>
                        </div>
                        {isLocked && (
                          <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[9px] font-bold">
                            PRO
                          </span>
                        )}
                        {isFreeTheme && !isPro && (
                          <span className="px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-700 text-[9px] font-medium">
                            FREE
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Checkmark for selected */}
                    {isSelected && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                        <span className="text-white text-xs">✓</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }


  // ============================================
  // CLASSIC + EMERALD THEME RENDER (shared layout, color-swapped for Emerald)
  // Classic palette: Cream #F1EBDD bg, Dark Forest Green #0F4C3A, Gold #C9A227
  // Emerald palette (color-swap): repainted via <style> overlay below.
  // ============================================
  const isClassic = appTheme === 'classic';

  if (isClassic || isEmerald) {
    const ArabesqueFlourish = ({ color = '#C9A227' }) => (
      <>
        <svg className="absolute top-1.5 left-1.5 w-5 h-5 pointer-events-none z-10 opacity-80" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
          <path d="M 2 10 C 2 5 5 2 10 2 M 2 2 L 7 2 M 2 2 L 2 7" />
          <circle cx="4" cy="4" r="1" fill={color} />
        </svg>
        <svg className="absolute top-1.5 right-1.5 w-5 h-5 pointer-events-none z-10 opacity-80" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
          <path d="M 22 10 C 22 5 19 2 14 2 M 22 2 L 17 2 M 22 2 L 22 7" />
          <circle cx="20" cy="4" r="1" fill={color} />
        </svg>
        <svg className="absolute bottom-1.5 left-1.5 w-5 h-5 pointer-events-none z-10 opacity-80" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
          <path d="M 2 14 C 2 19 5 22 10 22 M 2 22 L 7 22 M 2 22 L 2 17" />
          <circle cx="4" cy="20" r="1" fill={color} />
        </svg>
        <svg className="absolute bottom-1.5 right-1.5 w-5 h-5 pointer-events-none z-10 opacity-80" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
          <path d="M 22 14 C 22 19 19 22 14 22 M 22 22 L 17 22 M 22 22 L 22 17" />
          <circle cx="20" cy="20" r="1" fill={color} />
        </svg>
      </>
    );

    return (
      <>
        {isEmerald && (
          <style>{`
            /* ══════════════════════════════════════════════════════════════
               EMERALD PALETTE OVERLAY (2026-09-20, per user reference)
               Locked hexes:
                 Card BG        #0A3022   dark emerald
                 Gold accent    #CBB373   borders / titles / icons
                 Pro tile BG    #124332   deep olive
                 Cream          #E9E1CD   page + core icon circles
                 White          #FFFFFF   body / description text
               Repaints the shared Classic layout WITHOUT changing structure.
               ══════════════════════════════════════════════════════════════ */

            /* Outer page → dark emerald (per reference) */
            [data-testid="classic-homepage-root"] { background-color: #0A3022 !important; color: #FFFFFF !important; }

            /* Every "cream card" surface → dark emerald */
            [data-testid="classic-homepage-root"] .bg-\\[\\#F1EBDD\\] { background-color: #0A3022 !important; }
            [data-testid="classic-homepage-root"] .bg-\\[\\#0F4C3A\\]\\/15 { background-color: rgba(203, 179, 115, 0.15) !important; }
            [data-testid="classic-homepage-root"] .bg-\\[\\#0F4C3A\\]\\/25 { background-color: rgba(203, 179, 115, 0.25) !important; }
            [data-testid="classic-homepage-root"] .bg-\\[\\#0F4C3A\\]\\/10 { background-color: rgba(203, 179, 115, 0.15) !important; }
            [data-testid="classic-homepage-root"] .bg-\\[\\#125845\\] { background-color: #124332 !important; }

            /* CORE feature icons — the emerald medallion PNGs are supplied
               directly via <img src> from JSX (see the classic map above,
               isEmerald branch). Here we only flatten the surrounding cream
               rings so the medallion sits full-bleed inside the slot. */
            [data-testid="classic-homepage-root"] [data-testid^="core-feature-"] .bg-\\[\\#0F4C3A\\] {
              background-color: transparent !important;
              border-color: transparent !important;
            }
            [data-testid="classic-homepage-root"] [data-testid^="core-feature-"] .bg-\\[\\#0F4C3A\\] img {
              filter: none !important;
              width: 100% !important;
              height: 100% !important;
              object-fit: cover !important;
              border-radius: 9999px !important;
            }

            /* PRO feature icons — the emerald medallion is rendered as an
               <img> from JSX (isEmerald branch of the map). Here we just
               make sure the wrapping circle doesn't clip the artwork's
               native square-with-rounded-corners look. */
            [data-testid="classic-homepage-root"] [data-testid^="pro-feature-card-"] .bg-\\[\\#0F4C3A\\] {
              background-color: transparent !important;
              border-color: transparent !important;
              overflow: hidden !important;
            }
            [data-testid="classic-homepage-root"] [data-testid^="pro-feature-card-"] .text-\\[\\#C9A227\\] { color: #CBB373 !important; }

            /* PRO badge — solid gold pill, dark emerald label */
            [data-testid="classic-homepage-root"] [data-testid^="pro-feature-card-"] span.bg-\\[\\#0F4C3A\\] {
              background-color: #CBB373 !important;
              color: #0A3022 !important;
              border-color: #CBB373 !important;
              width: auto !important;
              height: auto !important;
            }
            [data-testid="classic-homepage-root"] [data-testid^="pro-feature-card-"] span.bg-\\[\\#0F4C3A\\]::after { content: none !important; }
            [data-testid="classic-homepage-root"] [data-testid^="pro-feature-card-"] span.bg-\\[\\#0F4C3A\\] svg { display: inline !important; color: #0A3022 !important; }
            [data-testid="classic-homepage-root"] [data-testid^="pro-feature-card-"] span.bg-\\[\\#C9A227\\]\\/20 {
              background-color: rgba(203, 179, 115, 0.2) !important;
              color: #CBB373 !important;
            }

            /* Tajweed "Try Now" hero — big olive medallion + gold "Try Now" pill */
            [data-testid="classic-homepage-root"] [data-testid="tajweed-mic-wrapper"] .bg-\\[\\#0F4C3A\\] {
              background-color: #124332 !important;
            }
            [data-testid="classic-homepage-root"] [data-testid="tajweed-mic-wrapper"] svg { color: #CBB373 !important; }
            [data-testid="classic-homepage-root"] [data-testid="tajweed-coach-try-now-btn"] {
              background-color: #CBB373 !important;
              color: #0A3022 !important;
              border-color: #CBB373 !important;
            }
            [data-testid="classic-homepage-root"] [data-testid="tajweed-coach-try-now-btn"] svg { color: #0A3022 !important; }

            /* Any remaining .bg-[#0F4C3A] circle icon holder (Next Prayer clock,
               small chips) → cream + gold icon. Written LAST so all the more-
               specific selectors above win via source order for their scopes. */
            [data-testid="classic-homepage-root"] .bg-\\[\\#0F4C3A\\] { background-color: #E9E1CD !important; }
            [data-testid="classic-homepage-root"] .bg-\\[\\#0F4C3A\\] svg { color: #CBB373 !important; }

            /* Borders — every gold-ish or dark-green border → new gold */
            [data-testid="classic-homepage-root"] .border-\\[\\#C9A227\\] { border-color: #CBB373 !important; }
            [data-testid="classic-homepage-root"] .border-\\[\\#0F4C3A\\] { border-color: #CBB373 !important; }

            /* Text — gold titles, white body */
            [data-testid="classic-homepage-root"] .text-\\[\\#0F4C3A\\] { color: #CBB373 !important; }
            [data-testid="classic-homepage-root"] .text-\\[\\#C9A227\\] { color: #CBB373 !important; }
            [data-testid="classic-homepage-root"] .text-\\[\\#4A4A4A\\] { color: #FFFFFF !important; }
            [data-testid="classic-homepage-root"] .text-\\[\\#1A1A1A\\] { color: #FFFFFF !important; }

            /* Gradient stops */
            [data-testid="classic-homepage-root"] .from-\\[\\#0F4C3A\\] {
              --tw-gradient-from: #CBB373 var(--tw-gradient-from-position) !important;
              --tw-gradient-to: rgba(203,179,115,0) var(--tw-gradient-to-position) !important;
              --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important;
            }
            [data-testid="classic-homepage-root"] .to-\\[\\#125845\\] {
              --tw-gradient-to: #124332 var(--tw-gradient-to-position) !important;
            }
            [data-testid="classic-homepage-root"] .hover\\:bg-\\[\\#125845\\]:hover { background-color: #124332 !important; }
            [data-testid="classic-homepage-root"] .group:hover .group-hover\\:bg-\\[\\#125845\\] { background-color: #124332 !important; }

            /* Hadith of the Day — legibility island (cream card with dark text) */
            [data-testid="classic-homepage-root"] [data-testid="hadith-card-classic"],
            html.theme-emerald [data-testid="hadith-card-classic"] { background-color: #E9E1CD !important; border-color: #CBB373 !important; }
            html.theme-emerald [data-testid="hadith-card-classic"] p.italic,
            html.theme-emerald [data-testid="hadith-card-classic"] p,
            html.theme-emerald [data-testid="hadith-card-classic"] p *,
            [data-testid="classic-homepage-root"] [data-testid="hadith-card-classic"] p.italic,
            [data-testid="classic-homepage-root"] [data-testid="hadith-card-classic"] p,
            [data-testid="classic-homepage-root"] [data-testid="hadith-card-classic"] p * { color: #0A3022 !important; opacity: 1 !important; }
            html.theme-emerald [data-testid="hadith-card-classic"] p.text-\\[\\#0F4C3A\\],
            [data-testid="classic-homepage-root"] [data-testid="hadith-card-classic"] p.text-\\[\\#0F4C3A\\] { color: #0A3022 !important; }
            [data-testid="classic-homepage-root"] [data-testid="hadith-card-classic"] button svg,
            html.theme-emerald [data-testid="hadith-card-classic"] button svg { color: #0A3022 !important; }
            [data-testid="classic-homepage-root"] [data-testid="hadith-card-classic"] .bg-\\[\\#0F4C3A\\] { background-color: #E9E1CD !important; }
          `}</style>
        )}
      <div 
        className="min-h-screen pb-12 relative bg-[#F1EBDD] text-[#1A1A1A]"
        onClick={() => showLanguageMenu && setShowLanguageMenu(false)}
        data-testid="classic-homepage-root"
      >
        <div className="relative max-w-lg lg:max-w-4xl xl:max-w-6xl mx-auto">
          {/* Next Prayer Card */}
          <div className="px-4 pt-3">
            <Link
              to="/prayer-times"
              className="relative flex items-center justify-between p-3.5 rounded-2xl bg-[#F1EBDD] border-2 border-[#C9A227] shadow-sm hover:scale-[1.01] transition-transform overflow-hidden"
              data-testid="next-prayer-card"
            >
              <ArabesqueFlourish color="#C9A227" />
              <div className="flex items-center gap-3 relative z-20">
                <div className="w-10 h-10 rounded-full bg-[#0F4C3A] flex items-center justify-center border border-[#C9A227] shadow-inner">
                  <Clock className="w-5 h-5 text-[#C9A227]" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#4A4A4A] uppercase tracking-wider block">
                    {t('prayer.nextPrayer', 'Next Prayer')}
                  </span>
                  <span className="text-base font-bold font-serif text-[#0F4C3A]">
                    {nextPrayer?.name || t('prayer.loading', 'Loading')}
                  </span>
                </div>
              </div>
              <div className="text-right relative z-20">
                <span className="font-extrabold text-lg text-[#0F4C3A] block">
                  {nextPrayer ? formatTime12h(nextPrayer.time) : '1:03 PM'}
                </span>
                <span className="text-xs text-[#4A4A4A]">
                  {t('prayer.in', 'in')} {countdown.hours}h {countdown.minutes}m
                </span>
              </div>
            </Link>
          </div>

          {/* Core Features */}
          <div className="px-4 mt-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-[#C9A227]">✨</span>
              <h2 className="text-base font-bold font-serif text-[#0F4C3A] tracking-wide">
                {t('home.coreFeatures', 'Core Features')}
              </h2>
              <span className="text-[#C9A227]">✨</span>
            </div>
            <div className={isEmerald ? "grid grid-cols-5 gap-0.5 sm:gap-1" : "grid grid-cols-2 sm:grid-cols-4 gap-4"}>
              {(isEmerald ? [
                { img: EMERALD_CORE_ICONS.prayer, label: t('nav.prayer', 'Prayers'), path: '/prayer-times', testid: 'core-feature-prayer' },
                { img: EMERALD_CORE_ICONS.hadith, label: t('nav.hadith', 'Hadith'),  path: '/hadith',       testid: 'core-feature-hadith' },
                { img: EMERALD_CORE_ICONS.duas,   label: t('nav.duas',   'Duas'),    path: '/duas',         testid: 'core-feature-duas'   },
                { img: EMERALD_CORE_ICONS.qibla,  label: t('nav.qibla',  'Qibla'),   path: '/qibla',        testid: 'core-feature-qibla'  },
                { img: EMERALD_CORE_ICONS.quran,  label: t('nav.quran',  'Quran'),   path: '/quran',        testid: 'core-feature-quran'  },
              ] : [
                { img: '/images/icons/quran.png',  label: t('nav.quran',  'Quran'),  path: '/quran',  testid: 'core-feature-quran'  },
                { img: '/images/icons/duas.png',   label: t('nav.duas',   'Duas'),   path: '/duas',   testid: 'core-feature-duas'   },
                { img: '/images/icons/qibla.png',  label: t('nav.qibla',  'Qibla'),  path: '/qibla',  testid: 'core-feature-qibla'  },
                { img: '/images/icons/hadith.png', label: t('nav.hadith', 'Hadith'), path: '/hadith', testid: 'core-feature-hadith' },
              ]).map((item, idx) => (
                <Link
                  key={idx}
                  to={item.path}
                  data-testid={item.testid}
                  className={isEmerald
                    ? "flex flex-col items-center justify-center hover:scale-105 transition-all text-center group py-1"
                    : "flex flex-col items-center justify-center hover:scale-105 transition-all text-center group py-2"}
                >
                  {/* Outer gold ring */}
                  <div className={isEmerald ? "p-0 mb-1 w-full" : "p-1.5 rounded-full border-2 border-[#C9A227] bg-[#F1EBDD] shadow-md mb-2.5"}>
                    {/* Inner gold ring */}
                    <div className={isEmerald ? "p-0 w-full" : "p-3 sm:p-4 rounded-full border-2 border-[#C9A227] bg-[#F1EBDD]"}>
                      {/* Green filled circle with gold icon */}
                      <div className={isEmerald
                        ? "w-full aspect-square flex items-center justify-center"
                        : "w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#0F4C3A] flex items-center justify-center group-hover:bg-[#125845] transition-colors overflow-hidden"}>
                        <img
                          src={item.img}
                          alt={item.label}
                          className={isEmerald ? "w-full h-full object-contain" : "w-14 h-14 sm:w-16 sm:h-16 object-contain"}
                          style={isEmerald ? { filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' } : { filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}
                          width="192"
                          height="192"
                          loading="eager"
                          decoding="async"
                          fetchPriority="high"
                        />
                      </div>
                    </div>
                  </div>
                  <span className={isEmerald ? "text-xs sm:text-sm font-bold font-serif text-[#CBB373] tracking-wide" : "text-sm font-bold font-serif text-[#0F4C3A] tracking-wide"}>
                    {item.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Tajweed Coach AI Hero Card */}
          <div className="px-4 mt-20">
            <div
              className="relative rounded-2xl px-5 pt-20 pb-5 bg-[#F1EBDD] border-2 border-[#C9A227] shadow-lg overflow-visible"
              data-testid="tajweed-coach-hero-card"
            >
              <ArabesqueFlourish color="#C9A227" />
              {/* Popping Microphone Circle with sound-wave pulse effect */}
              <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-32 h-32 z-30" data-testid="tajweed-mic-wrapper">
                {/* Outer sound-wave rings (pulse outward) */}
                <span className="tajweed-mic-ring tajweed-mic-ring-1" aria-hidden="true" />
                <span className="tajweed-mic-ring tajweed-mic-ring-2" aria-hidden="true" />
                <span className="tajweed-mic-ring tajweed-mic-ring-3" aria-hidden="true" />
                {/* Filled circle with mic */}
                <div className="relative w-32 h-32 rounded-full bg-[#0F4C3A] border-4 border-[#C9A227] shadow-xl flex items-center justify-center tajweed-mic-breathe">
                  <Mic className="w-16 h-16 text-[#C9A227] tajweed-mic-icon" strokeWidth={1.75} />
                </div>
              </div>
              <style>{`
                @keyframes tajweedMicPulse {
                  0%   { transform: scale(0.85); opacity: 0.7; }
                  70%  { transform: scale(1.5); opacity: 0; }
                  100% { transform: scale(1.5); opacity: 0; }
                }
                @keyframes tajweedMicBreathe {
                  0%, 100% { box-shadow: 0 8px 24px rgba(201,162,39,0.35), 0 0 0 0 rgba(201,162,39,0.6); }
                  50%      { box-shadow: 0 8px 32px rgba(201,162,39,0.55), 0 0 0 6px rgba(201,162,39,0.15); }
                }
                @keyframes tajweedMicIconSway {
                  0%, 100% { transform: rotate(-4deg); }
                  50%      { transform: rotate(4deg); }
                }
                .tajweed-mic-ring {
                  position: absolute;
                  inset: 0;
                  border-radius: 9999px;
                  border: 2px solid #C9A227;
                  animation: tajweedMicPulse 2.4s cubic-bezier(0.22, 1, 0.36, 1) infinite;
                  pointer-events: none;
                }
                .tajweed-mic-ring-2 { animation-delay: 0.8s; }
                .tajweed-mic-ring-3 { animation-delay: 1.6s; }
                .tajweed-mic-breathe {
                  animation: tajweedMicBreathe 2.4s ease-in-out infinite;
                }
                .tajweed-mic-icon {
                  transform-origin: center;
                  animation: tajweedMicIconSway 3.2s ease-in-out infinite;
                }
              `}</style>
              <div className="text-center relative z-20">
                <h3 className="text-xl font-bold font-serif text-[#0F4C3A] tracking-wide mb-3">
                  {t('tajweedCoach.title', 'Tajweed Coach AI')}
                </h3>
                <ul className="text-sm text-[#4A4A4A] space-y-1.5 mb-5 text-left inline-block">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#0F4C3A] flex-shrink-0" strokeWidth={3} />
                    <span>{t('magicCoach.benefits.realTimeGuidance', 'Real-time guidance')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#0F4C3A] flex-shrink-0" strokeWidth={3} />
                    <span>{t('magicCoach.benefits.wordByWord', 'Word-by-word tracking')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#0F4C3A] flex-shrink-0" strokeWidth={3} />
                    <span>{t('magicCoach.benefits.focusedLearning', 'Focused learning')}</span>
                  </li>
                </ul>
                <button
                  onClick={() => {
                    if (isPro) {
                      navigate('/quran?tajweed=true');
                    } else {
                      requestFeatureAccess(
                        { id: 'tajweed_coach', name: t('tajweedCoach.title', 'Tajweed Coach AI') },
                        () => navigate('/quran?tajweed=true')
                      );
                    }
                  }}
                  data-testid="tajweed-coach-try-now-btn"
                  className="w-full px-6 py-3 rounded-full bg-[#0F4C3A] border-2 border-[#C9A227] text-[#F1EBDD] font-extrabold text-sm flex items-center justify-center gap-1.5 hover:bg-[#125845] transition-all shadow-md active:scale-95"
                >
                  <span>{t('magicCoach.tryNow', 'Try Now')}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Pro Features Carousel */}
          <div className="px-4 mt-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[#C9A227]" />
              <h2 className={isEmerald ? "text-base font-bold font-serif text-[#CBB373] tracking-wide" : "text-base font-bold font-serif text-[#0F4C3A] tracking-wide"}>
                {t('home.proFeatures', 'Pro Features')}
              </h2>
              <Sparkles className="w-4 h-4 text-[#C9A227]" />
            </div>

            <ManualSnapCarousel>
              {(isEmerald ? premiumFeaturesEmerald : premiumFeatures).map((feature) => {
                const needsProGate = feature.isPro && !isPro && !(feature.featureId && isFeatureUnlockedViaAd(feature.featureId));
                const handleFeatureClick = (e) => {
                  if (needsProGate) {
                    e.preventDefault();
                    requestFeatureAccess(
                      { id: feature.featureId, name: t(feature.titleKey, feature.titleKey.split('.').pop()) },
                      () => navigate(feature.path)
                    );
                  } else {
                    navigate(feature.path);
                  }
                };

                return (
                  <div
                    key={feature.id}
                    onClick={handleFeatureClick}
                    data-testid={`pro-feature-card-${feature.id}`}
                    className={isEmerald
                      ? "flex-shrink-0 w-[270px] p-4 rounded-2xl bg-[#0B3B2E] border border-[#C9A227]/40 shadow-sm relative cursor-pointer hover:scale-[1.02] transition-transform"
                      : "flex-shrink-0 w-[270px] p-4 rounded-2xl bg-[#F1EBDD] border-2 border-[#C9A227] shadow-sm relative cursor-pointer hover:scale-[1.02] transition-transform"}
                  >
                    {feature.isPro && (
                      <span className={`absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full text-[9px] font-bold flex items-center gap-0.5 ${
                        needsProGate
                          ? 'bg-[#0F4C3A] text-[#C9A227] border border-[#C9A227]'
                          : 'bg-[#C9A227]/20 text-[#0F4C3A]'
                      }`}>
                        {needsProGate && <Lock className="w-2.5 h-2.5" />}
                        PRO
                      </span>
                    )}
                    <div className="flex items-center gap-3">
                      <div className={isEmerald
                        ? "w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                        : "w-12 h-12 rounded-full bg-[#0F4C3A] border-2 border-[#C9A227] flex items-center justify-center flex-shrink-0 overflow-hidden"}>
                        {isEmerald ? (
                          <img
                            src={EMERALD_PRO_ICONS[feature.id] || EMERALD_PRO_ICONS.themes}
                            alt={t(feature.titleKey, feature.titleKey.split('.').pop())}
                            className="w-full h-full object-cover"
                            loading="eager"
                            decoding="async"
                            fetchPriority="low"
                          />
                        ) : (
                          <feature.icon className="w-6 h-6 text-[#C9A227]" />
                        )}
                      </div>
                      <div>
                        <h3 className={isEmerald ? "font-bold text-sm font-serif text-[#CBB373]" : "font-bold text-sm font-serif text-[#0F4C3A]"}>
                          {t(feature.titleKey, feature.titleKey.split('.').pop())}
                        </h3>
                        <p className={isEmerald ? "text-xs text-[#E7DAB4]/70 line-clamp-2" : "text-xs text-[#4A4A4A] line-clamp-2"}>
                          {t(feature.descKey, feature.descKey.split('.').pop())}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </ManualSnapCarousel>
          </div>

          {/* Today's Story Section */}
          <div className="px-4 mt-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-[#C9A227]">🕮</span>
              <h2 className="text-base font-bold font-serif text-[#0F4C3A]">
                {t('home.todaysStory', "Today's Story")}
              </h2>
              <span className="text-[#C9A227]">🕮</span>
            </div>
            <TodaysStoryCard
              navigate={navigate}
              t={t}
              currentLang={i18n.language}
              onShare={openStoryShare}
              themeColors={{
                border: '#C9A227',
                headerBg: '#0F4C3A',
                headerAccent: '#C9A227',
                cardBg: '#F1EBDD',
                titleColor: '#0F4C3A',
                previewColor: '#4A4A4A',
                linkColor: '#C9A227',
              }}
            />
          </div>

          {/* Reciters Section */}
          <div className="px-4 mt-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-[#C9A227]">🎤</span>
              <h2 className="text-base font-bold font-serif text-[#0F4C3A]">
                {t('home.reciters', 'Reciters')}
              </h2>
              <span className="text-[#C9A227]">🎤</span>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-3 pt-1 scrollbar-hide items-start">
              {RECITERS.map((reciter, idx) => (
                <div
                  key={idx}
                  onClick={() => navigate('/settings#reciters')}
                  data-testid={`reciter-card-${reciter.id}`}
                  className="flex-shrink-0 w-20 cursor-pointer text-center relative group"
                >
                  <span className={`absolute -top-1 -right-1 z-20 px-1.5 py-0.5 rounded-full text-[8px] font-bold ${
                    reciter.isFree
                      ? 'bg-emerald-600 text-white'
                      : 'bg-[#0F4C3A] text-[#C9A227] border border-[#C9A227]'
                  }`}>
                    {reciter.isFree ? 'FREE' : 'PRO'}
                  </span>
                  <div className="w-16 h-16 mx-auto rounded-full border-2 border-[#C9A227] bg-[#0F4C3A]/10 overflow-hidden shadow-sm group-hover:scale-105 transition-transform flex items-center justify-center">
                    <img
                      src={reciter.image}
                      alt={reciter.fullName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        if (e.target.parentNode) {
                          e.target.parentNode.innerHTML = `<span class="text-xs font-bold text-[#0F4C3A]">${reciter.name.slice(0, 2)}</span>`;
                        }
                      }}
                    />
                  </div>
                  <p className="mt-1.5 font-bold font-serif text-xs truncate text-[#0F4C3A]">{reciter.name}</p>
                  <p className="text-[10px] truncate text-[#4A4A4A]">{reciter.country}</p>
                </div>
              ))}
              <div
                onClick={() => navigate('/settings#reciters')}
                className="flex-shrink-0 w-16 cursor-pointer flex flex-col items-center justify-center"
              >
                <div className="w-14 h-14 rounded-full border-2 border-dashed border-[#C9A227] bg-[#0F4C3A]/5 flex items-center justify-center hover:scale-105 transition-transform">
                  <ChevronRight className="w-6 h-6 text-[#C9A227]" />
                </div>
                <span className="mt-1.5 text-xs font-bold text-[#0F4C3A]">More</span>
              </div>
            </div>
          </div>

          {/* Hadith of the Day Section */}
          <div className="px-4 mt-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-[#C9A227]">📖</span>
              <h2 className="text-base font-bold font-serif text-[#0F4C3A]">
                {t('home.hadithOfDay', 'Hadith of the Day')}
              </h2>
              <span className="text-[#C9A227]">📖</span>
            </div>
            <div
              className="relative rounded-2xl p-5 bg-[#F1EBDD] border-2 border-[#C9A227] shadow-sm"
              data-testid="hadith-card-classic"
            >
              <ArabesqueFlourish color="#C9A227" />
              <button
                onClick={shareHadith}
                data-testid="share-hadith-classic"
                className="absolute top-3 right-3 p-2 rounded-full bg-[#0F4C3A]/10 hover:bg-[#0F4C3A]/20 transition-colors z-30"
              >
                <Share2 className="w-4 h-4 text-[#0F4C3A]" />
              </button>
              {dailyHadith && (
                <div className="relative z-20 pr-8">
                  <p className="text-sm font-serif italic leading-relaxed text-[#1A1A1A]">
                    "{dailyHadith.text}"
                  </p>
                  <p className="text-xs font-bold text-[#0F4C3A] mt-3">
                    — {dailyHadith.source || dailyHadith.book || 'Sahih al-Bukhari'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Stats Section */}
          <div className="px-4 mt-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-[#C9A227]">🏆</span>
              <h2 className="text-base font-bold font-serif text-[#0F4C3A]">
                {t('home.quickStats', 'Quick Stats')}
              </h2>
              <span className="text-[#C9A227]">🏆</span>
            </div>
            <div
              onClick={() => (isPro ? navigate('/progress') : promptUpgrade('progress'))}
              data-testid="quick-stats-classic"
              className="relative rounded-2xl p-4 bg-[#F1EBDD] border-2 border-[#C9A227] shadow-sm cursor-pointer hover:scale-[1.01] transition-transform"
            >
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: Flame, value: stats.streak, label: t('stats.dayStreak', 'Day Streak') },
                  { icon: Clock, value: stats.totalTime, label: t('stats.totalTime', 'Total Time') },
                  { icon: BookOpen, value: `${stats.surahs}/114`, label: t('stats.surahs', 'Surahs') },
                ].map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <div className="w-11 h-11 mx-auto mb-1.5 rounded-full bg-[#0F4C3A] border-2 border-[#C9A227] flex items-center justify-center shadow-sm">
                      <stat.icon className="w-5 h-5 text-[#C9A227]" />
                    </div>
                    <p className="text-lg font-bold font-serif text-[#0F4C3A]">{stat.value}</p>
                    <p className="text-[11px] text-[#4A4A4A] font-medium">{stat.label}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-1 mt-3 text-xs font-bold text-[#0F4C3A]">
                <span>{t('stats.tapToView', 'Unlock to view progress')}</span>
                <ChevronRight className="w-3.5 h-3.5 text-[#C9A227]" />
              </div>
            </div>
          </div>

          {/* Upgrade to Tanzeen+ Pro Banner */}
          <div className="px-4 mt-6 mb-8">
            <div className="rounded-2xl border-2 border-[#C9A227] overflow-hidden shadow-lg bg-[#F1EBDD]">
              <div className="p-4 bg-[#0F4C3A] text-center border-b border-[#C9A227]">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Crown className="w-6 h-6 text-[#C9A227]" />
                  <h3 className="text-xl font-bold font-serif text-[#C9A227]">Tanzeen+ Pro</h3>
                </div>
                <p className="text-xs text-[#C9A227] font-medium">
                  {t('pro.upgradeAccess', 'Upgrade for full access')}
                </p>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 gap-3.5 mb-5">
                  {[
                    { icon: '🔍', label: t('pro.zoomView', 'Zoom View Mode') },
                    { icon: '🥗', label: t('pro.halalScanner', 'Halal Scanner') },
                    { icon: '📱', label: t('pro.smartWidget', 'Smart Widget') },
                    { icon: '🤲', label: t('pro.customDua', "Custom Du'a Generator") },
                    { icon: '🕌', label: t('pro.masjidGuide', 'Masjid Guide') },
                    { icon: '🎙️', label: t('pro.reciters', 'All Reciters') },
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-xl bg-white/50 border border-[#C9A227]/30">
                      <span className="text-lg">{feature.icon}</span>
                      <span className="text-xs font-bold text-[#0F4C3A]">{feature.label}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => promptUpgrade(null)}
                  data-testid="upgrade-premium-btn-classic"
                  className="w-full py-3.5 rounded-full bg-[#0F4C3A] text-[#C9A227] border-2 border-[#C9A227] font-extrabold text-base flex items-center justify-center gap-2 hover:bg-[#125845] transition-all shadow-md active:scale-95"
                >
                  <Sparkles className="w-5 h-5 text-[#C9A227]" />
                  <span>{t('pro.unlock', '✨ Unlock Premium')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Visual Share Modal - Classic Theme */}
        {visualShareData && (
          <VisualShareModal
            isOpen={showVisualShare}
            onClose={() => {
              setShowVisualShare(false);
              setVisualShareData(null);
            }}
            contentType={visualShareData.contentType}
            title={visualShareData.title}
            arabicText={visualShareData.arabicText}
            translationText={visualShareData.translationText}
            transliterationText={visualShareData.transliterationText}
            reference={visualShareData.reference}
          />
        )}

        {/* Theme Selector Dialog - Classic Theme */}
        <Dialog open={themeDialogOpen} onOpenChange={setThemeDialogOpen}>
          <DialogContent className="sm:max-w-md" style={{ background: '#F1EBDD', border: '2px solid #C9A227' }}>
            <DialogHeader>
              <DialogTitle className="text-center font-serif text-xl text-[#0F4C3A]">
                {t('settings.selectTheme', 'Select Theme')}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-4">
              {Object.entries(APP_THEMES).map(([key, theme]) => {
                const isSelected = appTheme === key;
                const isFreeTheme = key === 'classic';
                const isLocked = !isPro && !isFreeTheme;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      if (isLocked) {
                        requestFeatureAccess({ 
                          id: 'all_themes', 
                          name: 'Premium Themes',
                          noAdUnlock: true 
                        }, () => {
                          setAppTheme(key);
                          setThemeDialogOpen(false);
                        });
                      } else {
                        setAppTheme(key);
                        setThemeDialogOpen(false);
                      }
                    }}
                    data-testid={`theme-option-${key}`}
                    className={`relative text-left rounded-xl border-2 overflow-hidden transition-all ${
                      isSelected
                        ? 'border-[#0F4C3A] shadow-lg ring-2 ring-[#0F4C3A]/20'
                        : isLocked 
                          ? 'border-gray-300 opacity-70'
                          : 'border-[#C9A227]/50 hover:border-[#0F4C3A]'
                    }`}
                  >
                    <div className="h-12 flex">
                      <div className="flex-1" style={{ backgroundColor: theme.preview?.background || '#1a1a1a' }} />
                      <div className="w-4" style={{ backgroundColor: theme.preview?.primary || '#333' }} />
                      <div className="w-4" style={{ backgroundColor: theme.preview?.secondary || '#666' }} />
                    </div>
                    <div className="p-2 bg-[#F1EBDD]">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-bold text-[#0F4C3A]">{theme.name}</p>
                          <p className="text-[10px] text-[#4A4A4A]">{theme.description}</p>
                        </div>
                        {isLocked && (
                          <span className="px-1.5 py-0.5 rounded-full bg-[#0F4C3A] text-[#C9A227] text-[9px] font-bold">
                            PRO
                          </span>
                        )}
                        {isFreeTheme && !isPro && (
                          <span className="px-1.5 py-0.5 rounded-full bg-emerald-600/20 text-emerald-800 text-[9px] font-bold">
                            FREE
                          </span>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#0F4C3A] flex items-center justify-center">
                        <span className="text-[#C9A227] text-xs">✓</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
    );
  }


  // ============================================
  // KIDS PARADISE THEME RENDER
  // Day: Soft pink/lavender #FDF2F8 bg, pastel cards (pink #FCE7F3, cyan #CFFAFE, yellow #FEF9C3)
  //      Accent: deep pink #DB2777, text: deep purple #4C1D95
  // Night: Indigo tones
  // ============================================
  const isKids = appTheme === 'kids';
  
  if (isKids) {
    // CORRECT COLORS per specification
    const kidsBg = isDark 
      ? 'bg-gradient-to-b from-[#1e1b4b] via-[#312e81] to-[#1e1b4b]' // Indigo for night
      : ''; // Using inline style for gradient
    const kidsBgStyle = isDark 
      ? {} 
      : { background: 'linear-gradient(180deg, #cffafe 0%, #dbeafe 40%, #e9d5ff 70%, #fce7f3 100%)' }; // Blue at top → Pink at bottom
    const kidsCardBg = isDark
      ? '#3730a3' // Indigo card for night
      : '#FCE7F3'; // Soft pink for day
    const kidsAccent = isDark ? '#fbbf24' : '#DB2777'; // Deep pink for day
    const kidsTextPrimary = isDark ? '#ffffff' : '#4C1D95'; // Deep purple for day
    const kidsTextSecondary = isDark ? '#c4b5fd' : '#9333ea';
    const kidsCardBorder = isDark ? '#818cf8' : '#DB2777';
    // Pastel card colors for variety
    const pastelColors = ['#FCE7F3', '#CFFAFE', '#FEF9C3', '#E9D5FF', '#DCFCE7', '#FEE2E2'];
    
    return (
      <div 
        className={`min-h-screen pb-8 relative overflow-hidden ${kidsBg}`}
        style={kidsBgStyle}
        onClick={() => showLanguageMenu && setShowLanguageMenu(false)}
      >
        {/* ============================================================
            KIDS PARADISE - PREMIUM MAGICAL ATMOSPHERE
            Soft, immersive, cinematic bedtime feeling
            ============================================================ */}
        
        {/* LAYER 1: Deep Sky Gradient Overlay (Night mode depth) */}
        {isDark && (
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse 120% 80% at 50% 20%, rgba(99, 102, 241, 0.15) 0%, transparent 50%)',
            }}
          />
        )}
        
        {/* LAYER 2: Premium Animated Stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Large glowing stars with depth */}
          <div 
            className="kids-star kids-star-gold kids-star-float"
            style={{ 
              top: '8%', left: '15%', 
              width: '8px', height: '8px',
              animationDelay: '0s',
              animationDuration: '4s, 8s'
            }} 
          />
          <div 
            className="kids-star kids-star-float"
            style={{ 
              top: '12%', right: '20%', 
              width: '6px', height: '6px',
              animationDelay: '1s',
              animationDuration: '3.5s, 7s'
            }} 
          />
          <div 
            className="kids-star kids-star-gold kids-star-float"
            style={{ 
              top: '25%', left: '8%', 
              width: '5px', height: '5px',
              animationDelay: '0.5s',
              animationDuration: '3s, 9s'
            }} 
          />
          <div 
            className="kids-star kids-star-float"
            style={{ 
              top: '18%', right: '8%', 
              width: '7px', height: '7px',
              animationDelay: '2s',
              animationDuration: '4.5s, 6s'
            }} 
          />
          <div 
            className="kids-star kids-star-gold"
            style={{ 
              top: '35%', left: '25%', 
              width: '4px', height: '4px',
              animationDelay: '1.5s',
              animationDuration: '2.5s'
            }} 
          />
          <div 
            className="kids-star"
            style={{ 
              top: '5%', left: '45%', 
              width: '5px', height: '5px',
              animationDelay: '0.8s',
              animationDuration: '3.2s'
            }} 
          />
          <div 
            className="kids-star kids-star-gold kids-star-float"
            style={{ 
              top: '15%', left: '70%', 
              width: '6px', height: '6px',
              animationDelay: '2.5s',
              animationDuration: '3.8s, 10s'
            }} 
          />
          <div 
            className="kids-star"
            style={{ 
              top: '30%', right: '15%', 
              width: '4px', height: '4px',
              animationDelay: '3s',
              animationDuration: '2.8s'
            }} 
          />
          
          {/* Tiny distant stars for depth */}
          <div className="kids-star" style={{ top: '10%', left: '35%', width: '2px', height: '2px', animationDelay: '0.3s', animationDuration: '2s' }} />
          <div className="kids-star" style={{ top: '22%', left: '55%', width: '2px', height: '2px', animationDelay: '1.2s', animationDuration: '2.2s' }} />
          <div className="kids-star" style={{ top: '8%', right: '35%', width: '2px', height: '2px', animationDelay: '0.7s', animationDuration: '1.8s' }} />
          <div className="kids-star" style={{ top: '28%', left: '40%', width: '2px', height: '2px', animationDelay: '2.1s', animationDuration: '2.5s' }} />
          <div className="kids-star" style={{ top: '20%', right: '45%', width: '2px', height: '2px', animationDelay: '1.8s', animationDuration: '2.3s' }} />
        </div>
        
        {/* LAYER 3: Soft Moon with Atmospheric Glow (Night mode) */}
        {isDark && (
          <div 
            className="kids-moon"
            style={{
              top: '6%',
              right: '12%',
              width: '50px',
              height: '50px',
              opacity: 0.9,
            }}
          />
        )}
        
        {/* LAYER 4: Drifting Clouds with Parallax */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Cloud Layer 1 - Slow drift */}
          <div 
            className="kids-cloud kids-cloud-drift"
            style={{
              top: '15%',
              left: '-50px',
              width: '200px',
              height: '40px',
              opacity: isDark ? 0.08 : 0.15,
            }}
          />
          <div 
            className="kids-cloud kids-cloud-drift-reverse"
            style={{
              top: '35%',
              right: '-30px',
              width: '180px',
              height: '35px',
              opacity: isDark ? 0.06 : 0.12,
              animationDelay: '5s',
            }}
          />
          {/* Cloud Layer 2 - Different speed for depth */}
          <div 
            className="kids-cloud kids-cloud-drift"
            style={{
              top: '55%',
              left: '-80px',
              width: '250px',
              height: '50px',
              opacity: isDark ? 0.04 : 0.08,
              animationDuration: '80s',
            }}
          />
        </div>
        
        {/* LAYER 5: Soft Ambient Light (top glow) */}
        <div 
          className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none"
          style={{
            width: '100%',
            maxWidth: '600px',
            height: '300px',
            background: isDark 
              ? 'radial-gradient(ellipse at center top, rgba(165, 180, 252, 0.12) 0%, transparent 70%)'
              : 'radial-gradient(ellipse at center top, rgba(219, 39, 119, 0.08) 0%, transparent 70%)',
          }}
        />
        
        <div className="relative max-w-lg lg:max-w-4xl xl:max-w-6xl mx-auto">
          {/* Content - No duplicate header, Layout.jsx handles top bar */}
          
          {/* Next Prayer - Kid friendly */}
          <div className="px-4 pt-4 pb-2">
            <Link to="/prayer-times" className="flex items-center justify-between px-4 py-3 rounded-2xl" style={{ background: kidsCardBg, border: `3px solid ${kidsCardBorder}` }}>
              <div className="flex items-center gap-3">
                <span className="text-2xl">🕐</span>
                <div>
                  <span className="text-sm font-bold" style={{ color: kidsTextPrimary }}>{t('prayer.nextPrayer', 'Next Prayer')}</span>
                  <p className="text-lg font-black" style={{ color: kidsAccent }}>{nextPrayer?.name || t('prayer.loading', 'Loading')}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xl font-black" style={{ color: kidsAccent }}>{nextPrayer ? formatTime12h(nextPrayer.time) : '--:--'}</p>
                <p className="text-xs" style={{ color: kidsTextSecondary }}>{countdown.hours}h {countdown.minutes}m</p>
              </div>
            </Link>
            
            {/* Fun Features Title */}
            <div className="flex items-center justify-center gap-2 mt-6 mb-3">
              <span className="text-xl">🎯</span>
              <span className="text-lg font-bold" style={{ color: kidsAccent }}>{t('kids.letsExplore', "Let's Explore!")}</span>
              <span className="text-xl">🎯</span>
            </div>
            
            {/* Premium Core Features - Kids Paradise Theme */}
            <div className="flex justify-around items-center py-2">
              {[
                { icon: 'book-quran', label: t('nav.quran', 'Quran'), path: '/quran', color: '#ec4899' },
                { icon: 'hands-praying', label: t('nav.duas', 'Duas'), path: '/duas', color: '#a855f7' },
                { icon: 'compass', label: t('nav.qibla', 'Qibla'), path: '/qibla', color: '#06b6d4' },
                { icon: 'book-open-reader', label: t('nav.hadith', 'Hadiths'), path: '/hadith', color: '#f59e0b' },
                { icon: 'clock', label: t('nav.prayer', 'Prayer'), path: '/prayer-times', color: '#10b981' },
              ].map((item, idx) => (
                <Link 
                  key={idx}
                  to={item.path}
                  className="flex-shrink-0 flex flex-col items-center gap-3 group"
                >
                  {/* Kids themed circle with colorful border */}
                  <div 
                    className="relative p-[3px] rounded-full transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
                    style={{
                      background: `linear-gradient(180deg, ${item.color} 0%, ${item.color}cc 50%, ${item.color} 100%)`,
                      boxShadow: `0 0 20px ${item.color}60, 0 4px 12px rgba(0, 0, 0, 0.2)`,
                    }}
                  >
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center relative overflow-hidden"
                      style={{ 
                        background: isDark 
                          ? 'linear-gradient(145deg, #3730a3 0%, #4c1d95 100%)'
                          : 'linear-gradient(145deg, #fce7f3 0%, #fbcfe8 100%)',
                      }}
                    >
                      {/* Playful sparkle highlight */}
                      <div 
                        className="absolute inset-0 rounded-full"
                        style={{
                          background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.5) 0%, transparent 40%)',
                        }}
                      />
                      {/* Font Awesome Icon */}
                      <FontAwesomeIcon 
                        icon={['fas', item.icon]}
                        className="text-2xl relative z-10 transition-all duration-300 group-hover:scale-110"
                        style={{ 
                          color: item.color,
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Fun colorful label */}
                  <span 
                    className="text-xs font-bold tracking-wide uppercase transition-all duration-300"
                    style={{ 
                      color: item.color,
                      letterSpacing: '0.05em',
                    }}
                  >
                    {item.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>
          
          {/* Tajweed Coach Hero Card - Flagship Feature */}
          <div className="px-4 mt-4">
            <MagicCoachHeroCard />
          </div>
          
          {/* Fun Features - Auto scrolling */}
          <div className="px-4 mt-4">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-xl">✨</span>
              <span className="text-lg font-bold" style={{ color: kidsAccent }}>{t('home.proFeatures', 'Pro Features')}</span>
              <span className="text-xl">✨</span>
            </div>
            <AutoScrollCarousel speed={60} direction="left">
              {premiumFeatures.map((feature, idx) => (
                <div 
                  key={feature.id} 
                  onClick={() => setShowKidsProModal(true)}
                  className="flex-shrink-0 w-[240px] rounded-2xl p-4 relative cursor-pointer transition-transform hover:scale-[1.02]" 
                  style={{ background: isDark ? kidsCardBg : pastelColors[idx % pastelColors.length], border: `3px solid ${kidsAccent}` }}
                >
                  {/* Lock icon on card */}
                  <span className="absolute top-2 right-2 text-xs opacity-60">🔒</span>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-2xl" style={{ background: kidsAccent + '30' }}>
                      <feature.icon className="w-5 h-5" style={{ color: kidsAccent }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-sm" style={{ color: isDark ? '#ffffff' : '#4C1D95' }}>{t(feature.titleKey, feature.titleKey.split('.').pop())}</h3>
                      <p className="text-xs" style={{ color: isDark ? '#c4b5fd' : '#7c3aed' }}>{t(feature.descKey, feature.descKey.split('.').pop())}</p>
                    </div>
                  </div>
                </div>
              ))}
            </AutoScrollCarousel>
          </div>
          
          {/* Today's Story - Kids (Coming Soon) */}
          <div className="px-4 mt-4">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-xl">📖</span>
              <span className="text-lg font-bold" style={{ color: kidsAccent }}>{t('home.todaysStory', "Today's Story")}</span>
              <span className="text-xl">📖</span>
            </div>
            <TodaysStoryCard navigate={navigate} isKids={true} t={t} currentLang={i18n.language} />
          </div>
          
          {/* Kids Tajweed Stars - Trophy Case Link */}
          <div className="px-4 mt-4">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-xl">⭐</span>
              <span className="text-lg font-bold" style={{ color: kidsAccent }}>{t('home.tajweedStars', "Tajweed Stars")}</span>
              <span className="text-xl">⭐</span>
            </div>
            <Link 
              to="/kids-progress"
              className="block rounded-2xl p-4 transition-all hover:scale-[1.02]"
              style={{ 
                background: `linear-gradient(135deg, #FFE4EC 0%, #E6F3FF 50%, #FFE4EC 100%)`,
                border: `3px solid ${kidsAccent}`
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-300 to-amber-500 flex items-center justify-center shadow-lg">
                    <span className="text-2xl">🏆</span>
                  </div>
                  <div>
                    <p className="font-bold text-purple-900">{t('home.myTrophyCase', "My Trophy Case")}</p>
                    <p className="text-sm text-purple-600">{t('home.collectStars', "Collect stars with your recitation!")}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <span className="text-xl">⭐</span>
                  <span className="text-xl">🌟</span>
                  <span className="text-xl">✨</span>
                </div>
              </div>
            </Link>
          </div>
          
          {/* Fun Reciters */}
          <div className="px-4 mt-4">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-xl">🎵</span>
              <span className="text-lg font-bold" style={{ color: kidsAccent }}>{t('home.reciters', 'Reciters')}</span>
              <span className="text-xl">🎵</span>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 pt-2 items-start" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
              <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>
              {RECITERS.map((reciter, idx) => {
                const isLocked = !reciter.isFree; // Always show PRO for non-free
                return (
                <div key={idx} className="flex-shrink-0 w-20 text-center relative cursor-pointer transition-all duration-300 hover:scale-105"
                  onClick={() => navigate('/settings#reciters')}
                >
                  {/* Always show PRO badge for non-free reciters */}
                  {!reciter.isFree && (
                    <div className="absolute -top-2 -right-2 z-50 px-1 py-0.5 rounded-full bg-orange-500 text-white text-[7px] font-bold shadow-lg flex items-center gap-0.5">
                      <Lock className="w-2 h-2" />
                      PRO
                    </div>
                  )}
                  {reciter.isFree && (
                    <div className="absolute -top-2 -right-2 z-50 px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-[7px] font-bold shadow-lg">
                      FREE
                    </div>
                  )}
                  <div 
                    className={`w-14 h-14 mx-auto rounded-full overflow-hidden ${isLocked && !isPro ? 'opacity-60' : ''}`}
                    style={{ 
                      border: '2px solid #10b981',
                      boxShadow: '0 2px 10px rgba(16, 185, 129, 0.2)',
                    }}
                  >
                    <img src={reciter.image} alt={reciter.fullName} className="w-full h-full object-cover" />
                  </div>
                  <p className={`mt-1 text-[10px] font-bold truncate ${isLocked ? 'opacity-60' : ''}`} style={{ color: kidsTextPrimary }}>{reciter.name}</p>
                </div>
              )})}
              {/* More button */}
              <div 
                className="flex-shrink-0 w-16 cursor-pointer transition-all duration-300 hover:scale-105 flex flex-col items-center justify-center"
                onClick={() => navigate('/settings#reciters')}
              >
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{
                    border: '2px dashed #10b981',
                    background: 'rgba(16, 185, 129, 0.1)',
                  }}
                >
                  <ChevronRight className="w-5 h-5" style={{ color: '#10b981' }} />
                </div>
                <p className="mt-1 font-bold text-[10px]" style={{ color: '#10b981' }}>More</p>
              </div>
            </div>
          </div>
          
          {/* Today's Hadith - Kid friendly */}
          <div className="px-4 mt-4">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-xl">📖</span>
              <span className="text-lg font-bold" style={{ color: kidsAccent }}>{t('home.hadithOfDay', 'Hadith of the Day')}</span>
              <span className="text-xl">📖</span>
            </div>
            <div className="rounded-2xl p-4 relative" style={{ background: isDark ? kidsCardBg : '#FEF9C3', border: `3px solid ${kidsCardBorder}` }}>
              {/* Share Button */}
              <button
                onClick={shareHadith}
                className="absolute top-3 right-3 p-2 rounded-full transition-all hover:scale-110"
                style={{ background: `${kidsAccent}20` }}
                data-testid="share-hadith-kids"
              >
                <Share2 className="w-4 h-4" style={{ color: kidsAccent }} />
              </button>
              {dailyHadith && (
                <>
                  <p className="text-sm leading-relaxed text-center pr-6" style={{ color: kidsTextPrimary }}>"{dailyHadith.text}"</p>
                  <p className="text-xs text-center mt-2" style={{ color: kidsAccent }}>📚 {dailyHadith.source}</p>
                </>
              )}
            </div>
          </div>
          
          {/* Progress - Kid friendly - Click to view Progress */}
          <div className="px-4 mt-4">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-xl">🏆</span>
              <span className="text-lg font-bold" style={{ color: kidsAccent }}>{t('kids.myProgress', 'My Progress')}</span>
              <span className="text-xl">🏆</span>
            </div>
            <div 
              className="rounded-2xl p-4 cursor-pointer transition-transform hover:scale-[1.02]" 
              style={{ background: kidsCardBg, border: `3px solid ${kidsCardBorder}` }}
              onClick={() => isPro ? navigate('/progress') : promptUpgrade('progress')}
              data-testid="quick-stats-kids"
            >
              <div className="grid grid-cols-3 gap-4">
                {[
                  { emoji: '🔥', value: stats.streak, label: t('kids.streak', 'Streak!') },
                  { emoji: '⏰', value: stats.totalTime, label: t('kids.time', 'Time') },
                  { emoji: '⭐', value: `${stats.surahs}/114`, label: t('kids.stars', 'Stars') },
                ].map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <span className="text-3xl block mb-1">{stat.emoji}</span>
                    <p className="text-2xl font-black" style={{ color: kidsAccent }}>{stat.value}</p>
                    <p className="text-xs font-bold" style={{ color: kidsTextPrimary }}>{stat.label}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-1 mt-3 text-xs font-bold" style={{ color: kidsAccent }}>
                <span>{t('home.tapForMore', "Tap for more! \ud83d\udc46")}</span>
              </div>
            </div>
          </div>
          
          {/* Ask Parents - PRO */}
          <div className="px-4 mt-4 mb-8">
            <div className="rounded-2xl overflow-hidden" style={{ background: kidsCardBg, border: `3px solid ${kidsCardBorder}` }}>
              <div className="p-4 flex items-center gap-3" style={{ background: isDark ? 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                <span className="text-3xl">👑</span>
                <div>
                  <h3 className="text-white font-bold text-lg">{t('kids.askParents', 'Ask Your Parents!')}</h3>
                  <p className="text-white/80 text-sm">{t('kids.unlockMore', 'Unlock more fun features')}</p>
                </div>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {[
                    { emoji: '📖', label: t('kids.talkingStories', 'Talking Story Books'), comingSoon: false },
                    { emoji: '📖', label: t('kids.quranCoach', 'Quran Coach'), comingSoon: false },
                    { emoji: '🔍', label: t('kids.halalScanner', 'Halal Scanner'), comingSoon: false },
                    { emoji: '🏆', label: t('kids.rewardsBadges', 'Rewards & Badges'), comingSoon: false },
                  ].map((feature, idx) => (
                    <div key={idx} className="relative flex items-center gap-2 p-2 rounded-xl" style={{ background: isDark ? 'rgba(251,191,36,0.1)' : 'rgba(251,191,36,0.2)' }}>
                      <span className="text-xl">{feature.emoji}</span>
                      <div className="flex-1">
                        <span className="text-xs font-bold block" style={{ color: kidsTextPrimary }}>{feature.label}</span>
                        {feature.comingSoon && (
                          <span className="text-[9px] font-medium" style={{ color: kidsAccent }}>{t('common.comingSoon', 'Coming Soon!')}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={() => setShowKidsProModal(true)} className="w-full py-3 rounded-xl flex items-center justify-center gap-2 text-white font-bold" style={{ background: isDark ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' : 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}>
                  <span className="text-xl">✨</span>
                  {t('kids.getPremium', 'Get Premium!')}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Theme Selector Dialog - Kids Theme - Matching QuranPage Design */}
        <Dialog open={themeDialogOpen} onOpenChange={setThemeDialogOpen}>
          <DialogContent className="sm:max-w-md" style={{ background: '#ffffff', border: '3px solid rgba(219, 39, 119, 0.3)' }}>
            <DialogHeader>
              <DialogTitle className="text-center font-serif text-xl" style={{ color: '#db2777' }}>
                {t('settings.selectTheme', 'Select Theme')} ✨
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-4">
              {Object.entries(APP_THEMES).map(([key, theme]) => {
                const isSelected = appTheme === key;
                const isFreeTheme = key === 'classic';
                const isLocked = !isPro && !isFreeTheme;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      if (isLocked) {
                        requestFeatureAccess({ 
                          id: 'all_themes', 
                          name: 'Premium Themes',
                          noAdUnlock: true 
                        }, () => {
                          setAppTheme(key);
                          setThemeDialogOpen(false);
                        });
                      } else {
                        setAppTheme(key);
                        setThemeDialogOpen(false);
                      }
                    }}
                    data-testid={`theme-option-${key}`}
                    className={`relative text-left rounded-xl border-2 overflow-hidden transition-all ${
                      isSelected
                        ? 'border-pink-500 shadow-lg ring-2 ring-pink-500/20'
                        : isLocked 
                          ? 'border-gray-200 opacity-70'
                          : 'border-gray-200 hover:border-pink-500/50'
                    }`}
                  >
                    {/* Color preview bars */}
                    <div className="h-12 flex">
                      <div className="flex-1" style={{ backgroundColor: theme.preview?.background || '#1a1a1a' }} />
                      <div className="w-4" style={{ backgroundColor: theme.preview?.primary || '#333' }} />
                      <div className="w-4" style={{ backgroundColor: theme.preview?.secondary || '#666' }} />
                    </div>
                    {/* Theme name section - cream background */}
                    <div className="p-2 bg-[#f5f0e6]">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-semibold text-black">{theme.name}</p>
                          <p className="text-[10px] text-gray-700">{theme.description}</p>
                        </div>
                        {isLocked && (
                          <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white text-[9px] font-bold">
                            PRO
                          </span>
                        )}
                        {isFreeTheme && !isPro && (
                          <span className="px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-700 text-[9px] font-medium">
                            FREE
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Checkmark for selected */}
                    {isSelected && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-pink-500 flex items-center justify-center">
                        <span className="text-white text-xs">✓</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Kids Pro Modal */}
        <KidsProModal 
          isOpen={showKidsProModal} 
          onClose={() => setShowKidsProModal(false)} 
          isDark={isDark} 
          t={t} 
        />
      </div>
    );
  }

  // ============================================
  // FALLBACK (shouldn't reach here)
  // ============================================
  return (
    <div className="min-h-screen pb-24 lg:pb-8">
      <div className="px-4 py-4 space-y-4 max-w-2xl mx-auto">
        {/* Simple prayer card for non-royal themes */}
        <Link to="/prayer-times">
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-br from-primary via-emerald-600 to-emerald-800 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm mb-1">{t('home.nextPrayer', "Next Prayer")}</p>
                  <div className="flex items-baseline gap-3">
                    <span className="text-2xl font-bold text-white">{nextPrayer?.name || 'Loading'}</span>
                    <span className="text-4xl font-black text-white">{nextPrayer ? formatTime12h(nextPrayer.time) : '--:--'}</span>
                  </div>
                  <p className="text-white/70 mt-1 text-sm">
                    in {countdown.hours}h {countdown.minutes}m {countdown.seconds}s
                  </p>
                </div>
                <ChevronRight className="w-6 h-6 text-white/50" />
              </div>
            </div>
          </Card>
        </Link>
        
        {/* Tajweed Coach Hero Card - Flagship Feature */}
        <MagicCoachHeroCard />
        
        {/* Features */}
        <div className="space-y-3">
          <Card className="cursor-pointer" onClick={() => navigate('/prayer-times')}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-purple-100">
                <MapPin className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold">{t('home.smartMasjidConnect', "Smart Masjid Connect")}</h3>
                <p className="text-sm text-muted-foreground">{t('home.smartMasjidConnectSub', "Prayer times from real mosques")}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>
        </div>
        
        {/* Verse of the Day */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-5 h-5 text-amber-500" />
              <span className="font-semibold">{t('home.verseOfDay', "Verse of the Day")}</span>
              {/* Share Button — opens VisualShareModal to generate the Ayat card.
                  Prior to QA Bug C this card had no share affordance on Emerald,
                  so users on the default theme could not share the daily verse. */}
              {dailyVerse && (
                <button
                  onClick={shareVerse}
                  className="ml-auto p-1.5 rounded-full hover:bg-black/5 transition-colors"
                  data-testid="share-verse-emerald"
                  title={t('common.share', 'Share')}
                  aria-label={t('common.share', 'Share')}
                >
                  <Share2 className="w-4 h-4 text-amber-600" />
                </button>
              )}
            </div>
            {dailyVerse && (
              <>
                <p className="text-center text-xl mb-3" style={{ fontFamily: "'Amiri', serif", direction: 'rtl' }}>
                  {dailyVerse.text}
                </p>
                <p className="text-sm text-muted-foreground text-center italic">
                  "{dailyVerse.translation}"
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Visual Share Modal */}
      {visualShareData && (
        <VisualShareModal
          isOpen={showVisualShare}
          onClose={() => {
            setShowVisualShare(false);
            setVisualShareData(null);
          }}
          contentType={visualShareData.contentType}
          title={visualShareData.title}
          arabicText={visualShareData.arabicText}
          translationText={visualShareData.translationText}
          transliterationText={visualShareData.transliterationText}
          reference={visualShareData.reference}
        />
      )}
    </div>
  );
};

export default HomePage;
